// Enhanced SaleHistory Component - FIXED for multi-round partial refunds and full-after-partial
// Key changes:
// 1) Allow multiple partial refunds on the same item (no forced "refund-all-remaining" after a prior partial).
// 2) Selection is tracked per *line item* (itemId + itemIndex) so duplicates of the same item are handled correctly.
// 3) Full-order refund after partial now reliably refunds only remaining available quantities (meta marks all as refunded) and sets amountRefunded to the order total.
// 4) UI fixes: single quantity input (removed duplicate), clear "Previously Refunded" indicator, robust clamping to available qty.

import React, { useEffect, useState, Component, useCallback, useRef } from 'react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { useNavigate } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import Select from 'react-select';
import moment from 'moment/moment';
import { useDispatch } from 'react-redux';
import { AgGridReact } from 'ag-grid-react';
import { toast } from 'react-toastify';
import { AddToOrder, clearOrder } from '../../../../Redux/actions/orderEditAction';
import { clearCart } from '../../../../Redux/actions/cartAction';
import userManagement from '../../../services/userManagement';
import ApiService from '../../../services/apiService';
import { invokeBroadCastMessage } from '../../../services/signalRService';
import ReceiptPrintService from '../../../services/ReceiptPrintService';
import PasscodePopup from '../../../Cashier/components/PassCode/passCodeModal';
import PaymentComponent from '../../../Cashier/components/flocashPayment';

// Convert SaleHistory order object into the shape expected by ReceiptPrintService
const buildReceiptDataFromOrder = (order, currency) => {
  if (!order) return null;

  const total = Number(order.orderAmount || 0);
  const tax = Number(order.taxAmount || order.tax || 0);
  const discount = Number(order.discountAmount || order.discount || 0);

  // If you have proper subtotal field from API, use that instead
  const subtotal = total ? total + discount - tax : 0;

  const orderItems = (order.orderItems || []).map((item) => ({
    itemName: item.menuItemName || item.itemName || `Item ${item.itemId}`,
    quantity: Number(item.quantity || 0),
    menuItemPrice: Number(item.menuItemPrice || item.itemAmount || 0),
    selectedVariant: item.selectedVariant || '', // if you don't have variants, it's fine
    specialInstructions: item.kitchenInstructions || item.specialInstructions || '',
  }));

  // Local mapping just for the receipt text
  const orderTypeMap = {
    0: 'Dine-In',
    1: 'Take-Away',
    2: 'Delivery',
    3: 'Online Order',
    4: 'Drive Through',
    5: 'Catering',
    6: 'Room Service',
    7: 'Pickup',
    8: 'Curbside Pickup',
    9: 'Pre-Order',
    10: 'Subscription',
  };

  return {
    orderId: order.originalId || order.id, // numeric id is fine
    orderDate: order.orderDate,
    orderItems,
    subtotal: subtotal || total,
    tax,
    discount,
    total,
    amountPaid: Number(order.amountPaid || total), // fallback
    change: Number(order.change || 0),
    customerName: order.customerName || 'Walk-in Customer',
    orderType: orderTypeMap[Number(order.orderType)] || 'Unknown',
    invoiceNumber: order.invoiceNumber || `INV-${order.originalId || order.id}`,
    paymentMethod: order.paymentMethodName || 'N/A',
    tableNumber: order.tableNumber || '',
  };
};

const Order = ({ Currency }) => {
  const [orderData, setOrderData] = useState([]);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [status, setstatus] = useState('');
  const [selectedRows, setSelectedRows] = useState({});
  const [RefundModal, setRefundModalOpen] = useState(false);
  const [ItemRefundModal, setItemRefundModalOpen] = useState(false);
  const [PrintModal, setPrintModalOpen] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);
  const [itemRefundLoading, setItemRefundLoading] = useState(false);
  const [printerSettings, setPrinterSettings] = useState({
    printerIP: localStorage.getItem('printerIP') || '192.168.1.100',
    printerPort: localStorage.getItem('printerPort') || '9100',
    copies: 1,
    includeOriginal: true,
    includeDuplicate: true,
  });
  const [formData, setFormData] = useState({ remarks: '' });
  /**
   * Item refund state is now tracked per *line item* using lineKey `${itemId}#${itemIndex ?? 0}`
   * selectedItems: [{ lineKey, itemId, itemIndex, quantity, maxQuantity }]
   */
  const [itemRefundData, setItemRefundData] = useState({
    selectedItems: [],
    refundReason: '',
    replacementItems: [],
    isReplacement: false,
  });

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showPasscodePopup, setShowPasscodePopup] = useState(false);
  const [refundType, setRefundType] = useState('order');
  const gridRef = useRef();

  // Payment related states
  const [menuItems, setMenuItems] = useState([]);
  const [loadingMenuItems, setLoadingMenuItems] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState([]);
  const [refundPaymentMethod, setRefundPaymentMethod] = useState(1);
  const [refundAmountCollected, setRefundAmountCollected] = useState(0);
  const [refundChange, setRefundChange] = useState(0);
  const [originalOrderPaymentMethod, setOriginalOrderPaymentMethod] = useState(null);
  const [originalTransactionId, setOriginalTransactionId] = useState(null);
  const [originalPaymentData, setOriginalPaymentData] = useState(null);

  // Auto-fill refund amount when selections change
  useEffect(() => {
    const netRefundAmount = calculateNetRefundAmount();
    if (refundPaymentMethod === 1 && netRefundAmount > 0) {
      setRefundAmountCollected(netRefundAmount);
      setRefundChange(0);
    }
  }, [itemRefundData.selectedItems, itemRefundData.replacementItems, refundPaymentMethod]);

  // Fetch payment methods
  const GetPaymentMethods = async () => {
    try {
      const apiService = new ApiService();
      const res = await apiService.get('GET_PAYMENT_METHOD');
      const filteredMethods = res.data.result.filter((item) => item?.active === true);
      setPaymentMethod(filteredMethods);
    } catch (error) {
      console.error('Error fetching payment methods:', error);
    }
  };

  // Fetch original payment data by order ID
  const getOriginalPaymentData = async (orderId) => {
    try {
      const apiService = new ApiService();
      const res = await apiService.getApiParamater('GET_PAYMENT', `?orderId=${orderId}`);

      if (res?.data?.result && res.data.result.length > 0) {
        const paymentData = res.data.result[0];
        setOriginalPaymentData(paymentData);
        setOriginalOrderPaymentMethod(paymentData.paymentMethodId || 1);
        setOriginalTransactionId(paymentData.transactionId || null);

        if (paymentData.paymentMethodId === 3) {
          setRefundPaymentMethod(1);
        } else {
          setRefundPaymentMethod(paymentData.paymentMethodId || 1);
        }

        return paymentData;
      } else {
        setOriginalOrderPaymentMethod(1);
        setRefundPaymentMethod(1);
        return null;
      }
    } catch (error) {
      console.error('Error fetching payment data:', error);
      setOriginalOrderPaymentMethod(1);
      setRefundPaymentMethod(1);
      return null;
    }
  };
  const handleBack = () => navigate(-1);

  // Fetch menu items for replacement dropdown
  const GetMenuItems = async () => {
    try {
      setLoadingMenuItems(true);
      const apiService = new ApiService();
      const res = await apiService.get('GET_ITEM_MASTER');

      if (res?.data?.result) {
        const activeItems = res.data.result
          .filter((item) => item.itemDescription && item.salePrice > 0)
          .map((item) => ({
            value: item,
            label: `${item.itemDescription} - ${Currency?.currency?.symbol || '$'}${Number(item.salePrice || 0).toFixed(2)}`,
            searchLabel: item.itemDescription?.toLowerCase() || '',
            itemName: item.itemDescription,
            itemDescription: item.itemDescription,
            recordId: item.recordId,
            record_Id: item.recordId,
            salePrice: item.salePrice,
            taxRate: item.vat ? Number(item.vat) : 0,
            vat: item.vat,
            ...item,
          }));

        setMenuItems(activeItems);
      }
    } catch (error) {
      console.error('Error fetching menu items:', error);
      toast.error('Failed to load menu items');
    } finally {
      setLoadingMenuItems(false);
    }
  };

  // PRINTER SERVICE CLASS (unchanged)
  class PrinterService {
    constructor(ip, port) {
      this.printerIP = ip;
      this.printerPort = port;
    }
    getESCPOSCommands() {
      return {
        INIT: '\x1B\x40',
        RESET: '\x1B\x40',
        CENTER: '\x1B\x61\x01',
        LEFT: '\x1B\x61\x00',
        RIGHT: '\x1B\x61\x02',
        BOLD_ON: '\x1B\x45\x01',
        BOLD_OFF: '\x1B\x45\x00',
        UNDERLINE_ON: '\x1B\x2D\x01',
        UNDERLINE_OFF: '\x1B\x2D\x00',
        SIZE_NORMAL: '\x1B\x21\x00',
        SIZE_DOUBLE: '\x1B\x21\x30',
        SIZE_LARGE: '\x1B\x21\x20',
        LINE_FEED: '\x0A',
        CUT_PAPER: '\x1D\x56\x41',
        DRAWER_OPEN: '\x1B\x70\x00\x19\xFA',
        BEEP: '\x1B\x42\x05\x05',
      };
    }
    generateReceiptContent(orderData, receiptType = 'ORIGINAL') {
      const cmd = this.getESCPOSCommands();
      let receipt = '';
      receipt += cmd.INIT;
      receipt += cmd.CENTER + cmd.SIZE_DOUBLE + cmd.BOLD_ON;
      receipt += 'YOUR RESTAURANT NAME\n';
      receipt += cmd.SIZE_NORMAL + cmd.BOLD_OFF;
      receipt += '123 Restaurant Street\n';
      receipt += 'City, State 12345\n';
      receipt += 'Tel: (555) 123-4567\n';
      receipt += cmd.LEFT;
      receipt += '\n' + cmd.CENTER + cmd.BOLD_ON + cmd.UNDERLINE_ON;
      receipt += `--- ${receiptType} ---\n`;
      receipt += cmd.BOLD_OFF + cmd.UNDERLINE_OFF + cmd.LEFT;
      receipt += '\n' + cmd.BOLD_ON;
      receipt += `Order #: ${orderData.id}\n`;
      receipt += cmd.BOLD_OFF;
      receipt += `Date: ${moment(orderData.orderDate).format('MMM DD, YYYY HH:mm')}\n`;
      receipt += `Customer: ${orderData.customerName || 'Walk-in'}\n`;
      receipt += `Type: ${this.mapOrderType(orderData.orderType)}\n`;
      receipt += `Source: ${this.mapOrderSource(orderData.orderSource)}\n`;
      receipt += '\n';
      receipt += '----------------------------------------\n';
      receipt += cmd.BOLD_ON;
      receipt += 'ITEM                    QTY   PRICE\n';
      receipt += cmd.BOLD_OFF;
      receipt += '----------------------------------------\n';
      let subtotal = 0;
      if (orderData.orderItems && orderData.orderItems.length > 0) {
        orderData.orderItems.forEach((item) => {
          const itemName = (item.menuItemName || `Item ${item.itemId}`).substring(0, 20);
          const quantity = item.quantity || 1;
          const price = Number(item.menuItemPrice || 0);
          const total = quantity * price;
          if (item.refunded) {
            receipt += `${itemName.padEnd(20)} ${quantity.toString().padStart(3)} ${total.toFixed(2).padStart(7)} [REFUNDED]\n`;
          } else if (item.voidItem) {
            receipt += `${itemName.padEnd(20)} ${quantity.toString().padStart(3)} ${total.toFixed(2).padStart(7)} [VOID]\n`;
          } else {
            receipt += `${itemName.padEnd(20)} ${quantity.toString().padStart(3)} ${total.toFixed(2).padStart(7)}\n`;
            subtotal += total;
          }
          if (item.kitchenInstructions) {
            receipt += `  Note: ${item.kitchenInstructions}\n`;
          }
        });
      }
      receipt += '----------------------------------------\n';
      const tax = subtotal * 0.1;
      const total = subtotal + tax;
      receipt += `Subtotal:                    ${subtotal.toFixed(2).padStart(7)}\n`;
      receipt += `Tax (10%):                   ${tax.toFixed(2).padStart(7)}\n`;
      receipt += cmd.BOLD_ON;
      receipt += `TOTAL:                       ${total.toFixed(2).padStart(7)}\n`;
      receipt += cmd.BOLD_OFF;
      if (orderData.amountRefunded > 0) {
        receipt += `Amount Refunded:             ${Number(orderData.amountRefunded).toFixed(2).padStart(7)}\n`;
        receipt += `Net Amount:                  ${(total - Number(orderData.amountRefunded)).toFixed(2).padStart(7)}\n`;
      }
      receipt += '\n';
      receipt += `Payment Status: ${this.mapPaymentStatus(orderData.paymentStatus)}\n`;
      receipt += `Order Status: ${this.mapOrderStatus(orderData.orderStatus)}\n`;
      if (orderData.refundReason) {
        receipt += `Refund Reason: ${orderData.refundReason}\n`;
      }
      receipt += '\n' + cmd.CENTER;
      receipt += 'Thank you for your business!\n';
      receipt += 'Please come again\n';
      receipt += '\n';
      if (receiptType === 'DUPLICATE') {
        receipt += cmd.BOLD_ON;
        receipt += '*** DUPLICATE RECEIPT ***\n';
        receipt += cmd.BOLD_OFF;
      }
      receipt += '\n\n';
      receipt += cmd.CUT_PAPER;
      return receipt;
    }
    mapOrderType(orderType) {
      const typeMap = {
        0: 'Dine-In',
        1: 'Take-Away',
        2: 'Delivery',
        3: 'Online Order',
        4: 'Drive Through',
        5: 'Catering',
        6: 'Room Service',
        7: 'Pickup',
        8: 'Curbside Pickup',
        9: 'Pre-Order',
        10: 'Subscription',
      };
      return typeMap[orderType] || 'Unknown';
    }
    mapOrderSource(orderSource) {
      const sourceMap = {
        0: 'POS System',
        1: 'Mobile App',
        2: 'Phone Call',
        3: 'Walk-in',
        4: 'Third Party',
        5: 'Cashier',
        6: 'Staff',
        7: 'Website',
        8: 'Kiosk',
        9: 'API',
      };
      return sourceMap[orderSource] || 'Unknown';
    }
    mapPaymentStatus(paymentStatus) {
      const statusMap = {
        0: 'Pending',
        1: 'Authorized',
        2: 'Paid',
        3: 'Refunded',
        4: 'Failed',
        5: 'Initiated',
        6: 'Processed',
        7: 'Canceled',
        8: 'Refund Initiated',
        9: 'Partially Refunded',
        10: 'Partially Paid',
      };
      return statusMap[paymentStatus] || 'Unknown';
    }
    mapOrderStatus(orderStatus) {
      const statusMap = {
        0: 'New Order',
        1: 'Accepted',
        2: 'Preparing',
        3: 'Ready For Delivery',
        4: 'Ready For Pickup',
        5: 'Out For Delivery',
        6: 'Completed',
        7: 'Canceled',
        8: 'On-Hold',
        9: 'Refund Initiated',
        10: 'Refunded',
        11: 'Payment Partially Refunded',
        12: 'Payment Partially Paid',
      };
      return statusMap[orderStatus] || 'Unknown';
    }
    async printReceipt(orderData, copies = 1, includeOriginal = true, includeDuplicate = true) {
      try {
        const prints = [];
        if (includeOriginal) {
          for (let i = 0; i < copies; i++) prints.push(this.generateReceiptContent(orderData, 'ORIGINAL'));
        }
        if (includeDuplicate) {
          for (let i = 0; i < copies; i++) prints.push(this.generateReceiptContent(orderData, 'DUPLICATE'));
        }
        for (const printContent of prints) {
          await this.sendToPrinter(printContent);
          await new Promise((r) => setTimeout(r, 500));
        }
        return { success: true, message: 'Receipt printed successfully' };
      } catch (error) {
        console.error('Print error:', error);
        return { success: false, message: error.message };
      }
    }
    async sendToPrinter(data) {
      return new Promise((resolve) => {
        try {
          fetch('/api/print-receipt', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ printerIP: this.printerIP, printerPort: this.printerPort, data, encoding: 'utf8' }),
          })
            .then((response) => {
              if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
              return response.json();
            })
            .then((result) => {
              if (result.success) {
                resolve(result);
              } else {
                throw new Error(result.message || 'Print failed');
              }
            })
            .catch(() => {
              this.fallbackPrint(data);
              resolve({ success: true, message: 'Opened print dialog as fallback' });
            });
        } catch (error) {
          console.error(error);
          resolve({ success: false, message: error.message });
        }
      });
    }
    fallbackPrint(receiptData) {
      const printWindow = window.open('', '_blank');
      const htmlContent = `
        <!DOCTYPE html><html><head><title>Receipt Print</title>
          <style>body{font-family:'Courier New',monospace;font-size:12px;margin:0;padding:20px;line-height:1.2}.receipt{width:300px;margin:0 auto;white-space:pre-wrap}@media print{body{margin:0;padding:10px}.receipt{width:100%}}</style>
        </head><body>
          <div class="receipt">${receiptData.replace(/\n/g, '<br>').replace(/ /g, '&nbsp;')}</div>
          <script>window.onload=function(){window.print();setTimeout(()=>window.close(),1000);}</script>
        </body></html>`;
      printWindow.document.write(htmlContent);
      printWindow.document.close();
    }
  }
  /**
   * Check if order has ANY partial refunds
   * Returns true if any item has been partially refunded (some quantity refunded, but not all)
   * This will DISABLE the "Full Refund" button
   */
  const hasPartialRefunds = (order) => {
    if (!order?.orderItems) return false;

    // Group items by itemId to check total vs refunded quantities
    const itemGroups = {};

    order.orderItems.forEach((item) => {
      if (!itemGroups[item.itemId]) {
        itemGroups[item.itemId] = {
          totalQuantity: 0,
          refundedQuantity: 0,
          voidedQuantity: 0,
        };
      }

      const qty = Number(item.quantity || 0);
      itemGroups[item.itemId].totalQuantity += qty;

      if (item.refunded) {
        itemGroups[item.itemId].refundedQuantity += qty;
      }
      if (item.voidItem) {
        itemGroups[item.itemId].voidedQuantity += qty;
      }
    });

    // Check if any item has partial refunds (some refunded but not all)
    return Object.values(itemGroups).some((group) => {
      const processedQty = group.refundedQuantity + group.voidedQuantity;
      // Partial refund exists if: 0 < processed < total
      return processedQty > 0 && processedQty < group.totalQuantity;
    });
  };

  /**
   * Get remaining refundable quantity for a specific item line
   * This calculates how much of this item can still be refunded by checking ALL lines with the same itemId
   */
  const getRemainingQuantityForLine = (item) => {
    if (!selectedRows?.orderItems) return 0;

    // Get all lines for this itemId
    const allLinesForItem = selectedRows.orderItems.filter((o) => o.itemId === item.itemId);

    // Calculate total original quantity across all lines
    const totalOriginalQuantity = allLinesForItem.reduce((sum, line) => {
      if (!line.refunded) {
        return sum + Number(line.quantity || 0);
      }
      return sum;
    }, 0);

    // Calculate total refunded/voided quantity
    const totalRefundedQuantity = allLinesForItem
      .filter((line) => line.refunded || line.voidItem)
      .reduce((sum, line) => {
        return sum + Number(line.quantity || 0);
      }, 0);

    // If THIS specific line is already refunded/voided, return 0
    if (item.refunded || item.voidItem) {
      return 0;
    }

    // Calculate remaining for this item across all lines
    const totalRemaining = totalOriginalQuantity - totalRefundedQuantity;

    // For non-refunded lines, show the remaining quantity
    // If there are multiple non-refunded lines, distribute the remaining quantity
    const nonRefundedLines = allLinesForItem.filter((line) => !line.refunded && !line.voidItem);

    if (nonRefundedLines.length === 1) {
      // If only one non-refunded line, it shows all remaining quantity
      return totalRemaining;
    } else {
      // If multiple non-refunded lines, show this line's quantity as max
      // (This handles the case where the order was split into multiple lines)
      return Math.min(Number(item.quantity || 0), totalRemaining);
    }
  };
  /**
   * Get refund history text for an item
   */
  const getRefundHistoryText = (itemId) => {
    const totalRefunded = getPreviouslyRefundedForItem(itemId);
    if (totalRefunded === 0) return '';
    return `${totalRefunded} previously refunded`;
  };
  // Mapping helpers (unchanged)
  const mapPaymentType = useCallback((paymentType) => {
    const status = paymentType == null ? -1 : Number(paymentType);
    const statusMap = {
      0: { text: 'Pending', color: '#856404', bg: '#fff3cd' },
      1: { text: 'Authorized', color: '#6E5199', bg: '#E8E2FF' },
      2: { text: 'Paid', color: '#28a745', bg: '#d4edda' },
      3: { text: 'Refunded', color: '#dc3545', bg: '#f8d7da' },
      4: { text: 'Failed', color: '#dc3545', bg: '#f8d7da' },
      5: { text: 'Initiated', color: '#6E5199', bg: '#E8E2FF' },
      6: { text: 'Processed', color: '#28a745', bg: '#d4edda' },
      7: { text: 'Canceled', color: '#6c757d', bg: '#e2e3e5' },
      8: { text: 'Refund Initiated', color: '#fd7e14', bg: '#ffeaa7' },
      9: { text: 'Partially Refunded', color: '#dc3545', bg: '#f8d7da' },
      10: { text: 'Partially Paid', color: '#6E5199', bg: '#E8E2FF' },
    };
    return statusMap[status] || { text: `Unknown (${status})`, color: '#6c757d', bg: '#e2e3e5' };
  }, []);

  const mapOrderStatus = useCallback((orderStatus) => {
    const status = orderStatus == null ? -1 : Number(orderStatus);
    const statusMap = {
      0: { text: 'New Order', color: '#6E5199', bg: '#E8E2FF' },
      1: { text: 'Accepted', color: '#28a745', bg: '#d4edda' },
      2: { text: 'Preparing', color: '#856404', bg: '#fff3cd' },
      3: { text: 'Ready For Delivery', color: '#17a2b8', bg: '#d1ecf1' },
      4: { text: 'Ready For Pickup', color: '#17a2b8', bg: '#d1ecf1' },
      5: { text: 'Out For Delivery', color: '#fd7e14', bg: '#ffeaa7' },
      6: { text: 'Completed', color: '#28a745', bg: '#d4edda' },
      7: { text: 'Canceled', color: '#dc3545', bg: '#f8d7da' },
      8: { text: 'On-Hold', color: '#6c757d', bg: '#e2e3e5' },
      9: { text: 'Refund Initiated', color: '#fd7e14', bg: '#ffeaa7' },
      10: { text: 'Refunded', color: '#dc3545', bg: '#f8d7da' },
      11: { text: 'Payment Partially Refunded', color: '#dc3545', bg: '#f8d7da' },
      12: { text: 'Payment Partially Paid', color: '#6E5199', bg: '#E8E2FF' },
    };
    return statusMap[status] || { text: `Unknown (${status})`, color: '#6c757d', bg: '#e2e3e5' };
  }, []);

  const mapOrderType = useCallback((orderType) => {
    const type = orderType == null ? -1 : Number(orderType);
    const typeMap = {
      0: 'Dine-In',
      1: 'Take-Away',
      2: 'Delivery',
      3: 'Online Order',
      4: 'Drive Through',
      5: 'Catering',
      6: 'Room Service',
      7: 'Pickup',
      8: 'Curbside Pickup',
      9: 'Pre-Order',
      10: 'Subscription',
    };
    return typeMap[type] || `Unknown (${type})`;
  }, []);

  const mapOrderSource = useCallback((orderSource) => {
    const source = orderSource == null ? -1 : Number(orderSource);
    const sourceMap = {
      0: 'POS System',
      1: 'Mobile App',
      2: 'Phone Call',
      3: 'Walk-in',
      4: 'Third Party',
      5: 'Cashier',
      6: 'Staff',
      7: 'Website',
      8: 'Kiosk',
      9: 'API',
    };
    return sourceMap[source] || `Unknown (${source})`;
  }, []);

  // PRINT HANDLERS (unchanged apart from minor safety)
  const handlePrintReceipt = async (orderData) => {
    try {
      const apiService = new ApiService();
      const res = await apiService.getApiParamater('GET_ORDERS', `?id=${orderData.originalId}`);
      const latestOrderData = res?.data?.result?.[0];
      if (latestOrderData) {
        setSelectedRows(latestOrderData);
        setPrintModalOpen(true);
      } else {
        toast.error('Could not fetch latest order data');
      }
    } catch (error) {
      console.error('Error fetching latest order data:', error);
      toast.error('Error fetching order data for printing');
    }
  };

  const handlePrinterSettingsChange = (field, value) => {
    setPrinterSettings((prev) => ({ ...prev, [field]: value }));
    if (field === 'printerIP' || field === 'printerPort') {
      localStorage.setItem(field, value);
    }
  };

  const executePrint = async () => {
    debugger;
    if (!selectedRows?.id) {
      toast.error('No order selected for printing');
      return;
    }
    setPrintLoading(true);
    try {
      const printerService = new PrinterService(printerSettings.printerIP, printerSettings.printerPort);
      const result = await printerService.printReceipt(selectedRows, printerSettings.copies, printerSettings.includeOriginal, printerSettings.includeDuplicate);
      if (result.success) {
        toast.success('Receipt printed successfully!');
        setPrintModalOpen(false);
      } else {
        toast.error(`Print failed: ${result.message}`);
      }
    } catch (error) {
      console.error('Print error:', error);
      toast.error('Failed to print receipt. Please check printer connection.');
    } finally {
      setPrintLoading(false);
    }
  };
  const executeBrowserPrint = async () => {
    if (!selectedRows?.id) {
      toast.error('No order selected for printing');
      return;
    }

    try {
      setPrintLoading(true);

      // At this point, selectedRows is already the latest order
      const receiptData = buildReceiptDataFromOrder(selectedRows, Currency?.currency);

      if (!receiptData) {
        toast.error('Could not build receipt data for printing');
        return;
      }

      // This will open the browser print dialog and let user pick ANY connected printer
      await ReceiptPrintService.printReceipt(receiptData, Currency?.currency);
      toast.success('Receipt sent to browser print dialog');
    } catch (error) {
      console.error('Browser print error:', error);
      toast.error('Failed to print receipt via browser');
    } finally {
      setPrintLoading(false);
    }
  };

  const closePrintModal = () => {
    setPrintModalOpen(false);
    setSelectedRows({});
  };

  // ===== Helpers for line keys and refunded stats =====
  const makeLineKey = (item) => `${item.itemId}#${item.itemIndex ?? 0}`;

  const getPreviouslyRefundedForItem = (itemId) => {
    const lines = (selectedRows?.orderItems || []).filter((o) => o.itemId === itemId);
    return lines.filter((o) => o.refunded || o.voidItem).reduce((s, o) => s + Number(o.quantity || 0), 0);
  };

  // ===== ITEM REFUND =====
  const handleItemRefund = async (orderData) => {
    if (!orderData.originalId) {
      toast.warning('Please select an order first');
      return;
    }
    if (orderData.paymentStatus === 3) {
      toast.info('This order is already fully refunded');
      return;
    }
    if (orderData.paymentStatus === 0) {
      toast.info('Cannot refund pending orders');
      return;
    }

    setSelectedRows(orderData);
    setItemRefundData({ selectedItems: [], refundReason: '', replacementItems: [], isReplacement: false });
    setRefundAmountCollected(0);
    setRefundChange(0);
    setOriginalPaymentData(null);
    setOriginalOrderPaymentMethod(null);
    setOriginalTransactionId(null);

    await getOriginalPaymentData(orderData.originalId);
    setItemRefundModalOpen(true);
    if (menuItems.length === 0) {
      GetMenuItems();
    }
  };

  /**
   * Handle item selection with strict validation
   * Ensures initial quantity doesn't exceed remaining quantity
   */
  const handleItemSelection = (lineItem, isSelected) => {
    const lineKey = makeLineKey(lineItem);
    const maxQ = getRemainingQuantityForLine(lineItem);

    // Prevent selection if no remaining quantity
    if (isSelected && maxQ === 0) {
      toast.warning('This item has no remaining quantity to refund');
      return;
    }

    setItemRefundData((prev) => {
      if (isSelected) {
        return {
          ...prev,
          selectedItems: [
            ...prev.selectedItems,
            {
              lineKey,
              itemId: lineItem.itemId,
              itemIndex: lineItem.itemIndex ?? 0,
              quantity: Math.min(1, maxQ), // Start with 1 or maxQ if maxQ < 1
              maxQuantity: maxQ,
            },
          ],
        };
      } else {
        return { ...prev, selectedItems: prev.selectedItems.filter((si) => si.lineKey !== lineKey) };
      }
    });
  };

  /**
   * Update quantity for selected line with strict clamping
   * CRITICAL: Prevents user from entering more than remaining quantity
   */
  const handleItemQuantityChange = (lineItem, newQuantity) => {
    const lineKey = makeLineKey(lineItem);
    const parsedQty = parseInt(newQuantity);

    // Validate input
    if (isNaN(parsedQty) || parsedQty < 1) {
      toast.warning('Quantity must be at least 1');
      return;
    }

    setItemRefundData((prev) => ({
      ...prev,
      selectedItems: prev.selectedItems.map((si) => {
        if (si.lineKey === lineKey) {
          // CRITICAL: Clamp to maximum available quantity
          if (parsedQty > si.maxQuantity) {
            toast.error(`Cannot refund more than ${si.maxQuantity} (remaining quantity for this item)`);
            return si; // Don't update, keep current value
          }
          return { ...si, quantity: parsedQty };
        }
        return si;
      }),
    }));
  };
  const isLineSelected = (lineItem) => itemRefundData.selectedItems.some((si) => si.lineKey === makeLineKey(lineItem));
  const getSelectedLineQuantity = (lineItem) => {
    const sel = itemRefundData.selectedItems.find((si) => si.lineKey === makeLineKey(lineItem));
    return sel ? sel.quantity : 1;
  };

  /**
   * Select all available items (only those with remaining quantity > 0)
   */
  const handleSelectAllItems = () => {
    if (!selectedRows.orderItems) return;

    const allLines = selectedRows.orderItems
      .map((item) => {
        const remaining = getRemainingQuantityForLine(item);
        return {
          lineKey: makeLineKey(item),
          itemId: item.itemId,
          itemIndex: item.itemIndex ?? 0,
          quantity: remaining,
          maxQuantity: remaining,
        };
      })
      .filter((item) => item.maxQuantity > 0); // Only include items with available quantity

    if (allLines.length === 0) {
      toast.info('No items available to refund');
      return;
    }

    setItemRefundData((prev) => ({ ...prev, selectedItems: allLines }));
  };
  const handleDeselectAllItems = () => setItemRefundData((prev) => ({ ...prev, selectedItems: [] }));

  const handleReplacementToggle = (isReplacement) =>
    setItemRefundData((prev) => ({ ...prev, isReplacement, replacementItems: isReplacement ? prev.replacementItems : [] }));

  const addReplacementItem = () =>
    setItemRefundData((prev) => ({
      ...prev,
      replacementItems: [...prev.replacementItems, { id: Date.now(), selectedMenuItem: null, quantity: 1, notes: '' }],
    }));

  const updateReplacementItem = (id, field, value) => {
    setItemRefundData((prev) => ({
      ...prev,
      replacementItems: prev.replacementItems.map((item) => {
        if (item.id === id) {
          if (field === 'selectedMenuItem') {
            return {
              ...item,
              selectedMenuItem: value,
              itemName: value?.itemDescription || value?.itemName || '',
              itemPrice: Number(value?.salePrice || 0),
              itemId: value?.recordId || value?.record_Id || 0,
            };
          }
          return { ...item, [field]: value };
        }
        return item;
      }),
    }));
  };

  const removeReplacementItem = (id) => setItemRefundData((prev) => ({ ...prev, replacementItems: prev.replacementItems.filter((i) => i.id !== id) }));

  // Calculations use line keys for precision
  const calculateItemRefundAmount = () => {
    if (!selectedRows.orderItems) return 0;
    const linesByKey = new Map();
    selectedRows.orderItems.forEach((li) => linesByKey.set(makeLineKey(li), li));
    return itemRefundData.selectedItems.reduce((sum, sel) => {
      const line = linesByKey.get(sel.lineKey);
      if (!line) return sum;
      return sum + Number(line.menuItemPrice) * Number(sel.quantity || 0);
    }, 0);
  };

  const calculateReplacementAmount = () =>
    itemRefundData.replacementItems.reduce((t, i) => t + Number(i.selectedMenuItem?.salePrice || i.itemPrice || 0) * Number(i.quantity || 0), 0);
  const calculateNetRefundAmount = () => Math.max(0, calculateItemRefundAmount() - calculateReplacementAmount());

  const handleRefundPaymentMethodChange = (methodId) => {
    setRefundPaymentMethod(methodId);
    if (methodId === 1) {
      const netRefundAmount = calculateNetRefundAmount();
      setRefundAmountCollected(netRefundAmount);
      setRefundChange(0);
    } else {
      setRefundAmountCollected(0);
      setRefundChange(0);
    }
  };

  const handleRefundAmountChange = (amount) => {
    const netRefundAmount = calculateNetRefundAmount();
    setRefundAmountCollected(amount);
    setRefundChange(amount > netRefundAmount ? amount - netRefundAmount : 0);
  };
  // Add this new function after the createRefundPayment function
  const callReturnAPI = async (orderData, selectedItems) => {
    try {
      // Get the items that are being refunded
      debugger;
      const normalizeSerials = (v) => {
        if (v == null) return [];

        // array -> strings (supports ["S1","S2"] OR [{serialNumber:"S1"}, ...])
        if (Array.isArray(v)) {
          return v
            .map((x) => {
              if (x == null) return '';
              if (typeof x === 'object') return String(x.serialNumber ?? x.serial ?? x.value ?? x.number ?? '').trim();
              return String(x).trim();
            })
            .filter(Boolean);
        }

        // string like "serial1,serial2"
        if (typeof v === 'string') {
          return v
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
        }

        // single object -> try common fields, else stringify
        if (typeof v === 'object') {
          const s = v.serialNumber ?? v.serial ?? v.value ?? v.number ?? '';
          return [String(s).trim()].filter(Boolean);
        }

        return [String(v).trim()].filter(Boolean);
      };

      const returnedItems = selectedItems.map((sel) => {
        const line = orderData.orderItems.find((li) => `${li.itemId}#${li.itemIndex ?? 0}` === sel.lineKey);
        // if (!line) throw new Error(`Line not found: ${sel.lineKey}`);

        const qty = Number(sel.quantity || 0);
        if (qty <= 0) throw new Error(`Invalid qty for ${sel.lineKey}`);

        const allSerials = normalizeSerials(line.itemSerialNumbers);

        // Always pass serials as a STRING, comma-separated when qty > 1
        // (and ensure we have at least qty serials)
        if (allSerials.length > 0 && allSerials.length < qty) {
          throw new Error(`Not enough serials for ${line.menuItemName}. Need ${qty}, found ${allSerials.length}`);
        }

        const payload = {
          material: line.itemCode || String(line.itemId),
          quantity: qty,
          itemSerialNumbers: allSerials.length > 0 ? allSerials.slice(0, qty).join(',') : null,
        };

        return payload;
      });

      const returnAPIBody = {
        endpointUrl: 'https://tp-apg-testza.multichoice.co.za/ws/SalesProcessingConfirmationAPI.HTTP_Port/1.0',
        templateName: 'SubscriberReturn',
        parameters: {
          countryIdentifier: 'zaf',
          referenceID: orderData.referenceID || orderData.originalId.toString(),
          orderReason: '912', // You may want to make this configurable
          returnedItemList: returnedItems,
        },
        token: 'asfsdfsdfsfsddfsdfsdf', // Replace with actual token from config/env
      };

      const apiService = new ApiService();
      const response = await apiService.post('CREATE_SOAP_SALE_RETURN', returnAPIBody); // Replace with your actual endpoint name
      debugger;
      if (response?.data?.statusCode === 201 || response?.data?.statusCode === 200 || response?.statusCode === 200) {
        return { success: true, data: response.data };
      } else if (response?.data?.statusCode === 417) {
        return { success: false, message: response?.data?.message || 'Return API call failed' };
      } else {
        return { success: false, message: response?.data?.message || 'Return API call failed' };
      }
    } catch (error) {
      console.error('Error calling return API:', error);
      return { success: false, message: error.message || 'Return API call failed' };
    }
  };
  const createRefundPayment = async (orderId, refundAmount) => {
    try {
      const apiService = new ApiService();
      const selectedPaymentMethod = paymentMethod.find((p) => p.id === refundPaymentMethod);
      let paymentStatus = 3;
      let refundNotes = `Item refund via ${selectedPaymentMethod?.name || 'Cash'}`;
      if (refundPaymentMethod === 3) {
        paymentStatus = 5;
        refundNotes = 'Flocash item refund initiated';
      }
      const refundPaymentBody = {
        orderId,
        paymentMethodId: refundPaymentMethod,
        paymentMethodName: selectedPaymentMethod?.name || 'Cash',
        amount: -refundAmount,
        paymentAmount: -refundAmount,
        amountPaid: refundAmountCollected || refundAmount,
        change: refundChange || 0,
        paymentStatus,
        transactionId: `ITEM_REFUND_${Date.now()}`,
        paymentDate: new Date().toISOString(),
        currency: Currency?.currency?.code || 'PKR',
        customerId: selectedRows.customerId || 'Guest',
        customerName: selectedRows.customerName || 'Guest',
        notes: refundNotes,
        paymentType: refundPaymentMethod,
        isRefund: true,
      };
      const paymentRes = await apiService.post('CREATE_PAYMENT', refundPaymentBody);
      return paymentRes?.data?.statusCode === 201;
    } catch (error) {
      console.error('Refund payment creation failed:', error);
      return false;
    }
  };

  const handleFlocashRefundSuccess = async (paymentResult) => {
    try {
      if (paymentResult.error) {
        toast.error('Flocash refund failed. Please try again.');
        return;
      }
      if (paymentResult.tranStatus === 'APPROVED') {
        const apiService = new ApiService();
        const refundAmount = calculateNetRefundAmount();
        const flocashRefundPaymentBody = {
          orderId: selectedRows.originalId,
          paymentMethodId: 3,
          paymentMethodName: 'Flocash',
          amount: -refundAmount,
          paymentAmount: refundAmount,
          amountPaid: refundAmount,
          change: 0,
          paymentStatus: 2,
          transactionId: paymentResult.tranId || paymentResult.messageId || `FLOCASH_REFUND_${Date.now()}`,
          paymentDate: new Date().toISOString(),
          currency: Currency?.currency?.code || 'PKR',
          customerId: selectedRows.customerId || 'Guest',
          customerName: selectedRows.customerName || 'Guest',
          notes: `Flocash item refund - Message ID: ${paymentResult.messageId}, Status: ${paymentResult.tranStatus}`,
          paymentType: 3,
          isRefund: true,
          flocashData: {
            messageId: paymentResult.messageId,
            posId: paymentResult.posId,
            tranType: paymentResult.tranType,
            tranStatus: paymentResult.tranStatus,
            tranId: paymentResult.tranId,
            currency: paymentResult.currency,
            requestAmount: paymentResult.requestAmount,
            approvalCode: paymentResult.approvalCode,
            responseCode: paymentResult.responseCode,
            responseMessage: paymentResult.responseMessage,
          },
        };
        const paymentRes = await apiService.post('CREATE_PAYMENT', flocashRefundPaymentBody);
        if (paymentRes?.data?.statusCode === 201) {
          toast.success('Flocash refund processed successfully!');
          await processItemRefund(true);
        } else {
          toast.error('Failed to record Flocash refund. Please contact support.');
        }
      } else {
        toast.error('Flocash refund declined. Please try again.');
      }
    } catch (error) {
      console.error('Error processing Flocash refund:', error);
      toast.error('Failed to process Flocash refund. Please try again.');
    }
  };

  const executeItemRefund = async () => {
    if (!itemRefundData.selectedItems.length) {
      toast.warning('Please select at least one item to process');
      return;
    }
    // CRITICAL: Validate that no selected quantity exceeds remaining quantity
    const invalidSelections = itemRefundData.selectedItems.filter((sel) => {
      const item = selectedRows.orderItems.find((o) => makeLineKey(o) === sel.lineKey);
      if (!item) return true;
      const remaining = getRemainingQuantityForLine(item);
      return sel.quantity > remaining;
    });

    if (invalidSelections.length > 0) {
      toast.error('Some selected quantities exceed available quantities. Please check and try again.');
      return;
    }
    if (!itemRefundData.refundReason.trim()) {
      toast.warning(itemRefundData.isReplacement ? 'Please provide a replacement reason' : 'Please provide a refund reason');
      return;
    }
    if (itemRefundData.isReplacement && itemRefundData.replacementItems.length === 0) {
      toast.warning('Please add at least one replacement item');
      return;
    }
    if (itemRefundData.isReplacement) {
      const invalidItems = itemRefundData.replacementItems.some((item) => !item.selectedMenuItem || !item.selectedMenuItem.recordId || item.quantity <= 0);
      if (invalidItems) {
        toast.warning('Please select valid menu items and set quantities for all replacement items');
        return;
      }
    }
    const netRefundAmount = calculateNetRefundAmount();
    if (refundPaymentMethod === 1 && netRefundAmount > 0) {
      if (refundAmountCollected <= 0) {
        toast.warning('Please enter the cash amount given for refund');
        return;
      }
      if (refundAmountCollected < netRefundAmount) {
        toast.warning(`Cash amount must be at least $${netRefundAmount.toFixed(2)}`);
        return;
      }
    }
    if (!isAuthenticated) {
      setRefundType('item');
      setItemRefundModalOpen(false);
      setShowPasscodePopup(true);
      return;
    }
    await processItemRefund();
  };

  const processItemRefund = async (flocashRefundCompleted = false) => {
    try {
      setItemRefundLoading(true);

      // STEP 1: Call the return API first
      const returnAPIResult = await callReturnAPI(selectedRows, itemRefundData.selectedItems);

      if (!returnAPIResult.success) {
        toast.error(`Return API failed: ${returnAPIResult.message}`);
        setItemRefundLoading(false);
        return;
      }

      toast.success('Return API call successful, processing refund...');

      // STEP 2: Proceed with existing refund logic
      const apiService = new ApiService();
      const selectedKeySet = new Set(itemRefundData.selectedItems.map((si) => si.lineKey));
      const isSelectedLine = (item) => selectedKeySet.has(makeLineKey(item));

      const refundAmount = calculateItemRefundAmount();
      const replacementAmount = calculateReplacementAmount();
      const netRefundAmount = calculateNetRefundAmount();
      const updatedOrderItems = selectedRows.orderItems.map((item) => ({
        itemId: item.itemId,
        itemName: item.menuItemName,
        itemAmount: Number(item.menuItemPrice),
        menuItemTax: Number(item.menuItemTax) || 0,
        quantity: Number(item.quantity),
        kitchenInstructions: item.kitchenInstructions || '',

        refunded: !itemRefundData.isReplacement ? (isSelectedLine(item) ? true : item.refunded) : item.refunded,
        voidItem: itemRefundData.isReplacement ? (isSelectedLine(item) ? true : item.voidItem) : item.voidItem,
        itemIndex: item.itemIndex || 0,
        invoiceId: item.invoiceId || 0,
      }));

      if (itemRefundData.isReplacement && itemRefundData.replacementItems.length > 0) {
        const newItemIndex = Math.max(...updatedOrderItems.map((item) => item.itemIndex || 0)) + 1;

        itemRefundData.replacementItems.forEach((replacementItem, index) => {
          const menuItem = replacementItem.selectedMenuItem;
          updatedOrderItems.push({
            itemId: menuItem.recordId || menuItem.record_Id,
            itemName: menuItem.itemDescription || menuItem.itemName,
            itemAmount: Number(menuItem.salePrice),
            menuItemTax: Number(menuItem.vat || menuItem.taxRate || 0),
            quantity: Number(replacementItem.quantity),
            kitchenInstructions: replacementItem.notes || 'Replacement item',
            voidItem: false,
            refunded: false,
            itemIndex: newItemIndex + index,
            invoiceId: 0,
          });
        });
      }

      const orderItemsMeta = selectedRows.orderItemsMeta || [];

      // Calculate the CURRENT refund amount from selectedRows (existing amountRefunded)
      const currentRefundedAmount = Number(selectedRows.amountRefunded) || 0;

      // NEW refund amount is the net refund amount from selected items
      const newRefundAmount = Math.max(0, netRefundAmount);

      // TOTAL refunded amount is existing + new refund
      const totalRefundedAmount = currentRefundedAmount + newRefundAmount;

      // NEW order amount is current order amount + replacement amount
      const newOrderAmount = Number(selectedRows.orderAmount) + replacementAmount;

      // Payment status based on total refunded vs new order amount
      const newPaymentStatus = totalRefundedAmount >= newOrderAmount ? 3 : totalRefundedAmount > 0 ? 9 : selectedRows.paymentStatus;

      // Call UPDATE_ORDER_META API first
      const updateOrderMetaPayload = {
        id: parseInt(selectedRows.originalId),
        orderItems: updatedOrderItems,
        orderItemsMeta: orderItemsMeta,
        loggedInUserId: userManagement.getUserName() || 'Guest',
      };

      console.log('UPDATE_ORDER_META Payload:', updateOrderMetaPayload);

      const updateOrderMetaRes = await apiService.put('UPDATE_ORDER_META', updateOrderMetaPayload);

      if (updateOrderMetaRes?.data?.statusCode === 200 || updateOrderMetaRes?.status === 200) {
        // Now call UPDATE_ORDER API for order-level changes
        const orderUpdateData = {
          id: parseInt(selectedRows.originalId),
          orderAmount: newOrderAmount,
          amountRefunded: totalRefundedAmount, // This is existing + new refund
          refundReason: itemRefundData.refundReason,
          paymentStatus: newPaymentStatus,
          loggedInUserId: userManagement.getUserName() || 'Guest',
        };

        console.log('UPDATE_ORDER Payload:', orderUpdateData);
        console.log('Refund Calculation Details:', {
          currentRefundedAmount,
          newRefundAmount,
          totalRefundedAmount,
          selectedItemsValue: refundAmount,
          replacementValue: replacementAmount,
          netRefundAmount,
        });

        const orderRes = await apiService.put('UPDATE_ORDER', orderUpdateData);

        if (orderRes?.data?.statusCode === 200 || orderRes?.status === 200) {
          // Only create payment record for the NET refund amount (not total)
          if (newRefundAmount > 0 && !flocashRefundCompleted) {
            await createRefundPayment(selectedRows.originalId, newRefundAmount);
          }

          const processedItemsCount = itemRefundData.selectedItems.length;
          const replacementItemsCount = itemRefundData.replacementItems.length;

          let successMessage = '';
          if (itemRefundData.isReplacement) {
            successMessage = `${processedItemsCount} item(s) replaced with ${replacementItemsCount} new item(s)`;
            if (newRefundAmount > 0) {
              successMessage += ` (Net refund: ${newRefundAmount.toFixed(2)})`;
            }
          } else {
            successMessage = `${processedItemsCount} item(s) refunded for ${newRefundAmount.toFixed(2)}`;
          }

          toast.success(successMessage);

          const broadcastMessage = itemRefundData.isReplacement
            ? `Items replaced for Order #${selectedRows.originalId}`
            : `Items refunded for Order #${selectedRows.originalId}`;
          invokeBroadCastMessage(broadcastMessage);

          setItemRefundModalOpen(false);
          setItemRefundData({ selectedItems: [], refundReason: '', replacementItems: [], isReplacement: false });
          setSelectedRows({});
          setRefundAmountCollected(0);
          setRefundChange(0);
          setOriginalPaymentData(null);
          setOriginalOrderPaymentMethod(null);
          setOriginalTransactionId(null);
          gridRef.current?.api?.deselectAll();
          GetOrders();
        } else {
          toast.error('Failed to update order. Please try again!');
        }
      } else {
        toast.error('Failed to update order items and meta. Please try again.');
      }
    } catch (error) {
      console.error('Error processing item refund/replacement:', error);
      toast.error('Failed to process request. Please try again later.');
    } finally {
      setItemRefundLoading(false);
    }
  };
  const closeItemRefundModal = () => {
    setItemRefundModalOpen(false);
    setItemRefundData({ selectedItems: [], refundReason: '', replacementItems: [], isReplacement: false });
    setRefundAmountCollected(0);
    setRefundChange(0);
    setOriginalPaymentData(null);
    setOriginalOrderPaymentMethod(null);
    setOriginalTransactionId(null);
  };

  // FILTER OPTIONS
  const ItemSourse = [
    { value: '', label: 'All Orders' },
    { value: '0', label: 'New Order' },
    { value: '1', label: 'Accepted' },
    { value: '2', label: 'Preparing' },
    { value: '3', label: 'Ready For Delivery' },
    { value: '4', label: 'Ready For Pickup' },
    { value: '5', label: 'Out For Delivery' },
    { value: '6', label: 'Completed' },
    { value: '7', label: 'Canceled' },
    { value: '8', label: 'On-Hold' },
    { value: '9', label: 'Refund Initiated' },
    { value: '10', label: 'Refunded' },
    { value: '11', label: 'Payment Partially Refunded' },
    { value: '12', label: 'Payment Partially Paid' },
  ];

  // SELECTION HANDLER
  const onSelectionChanged = useCallback(() => {
    if (!gridRef.current) return;
    const selectedRows = gridRef.current.api.getSelectedRows();
    if (selectedRows.length > 0) {
      const selectedOrder = selectedRows[0];
      setSelectedRows(selectedOrder);
    } else {
      setSelectedRows({});
    }
  }, []);

  // STATUS CELL RENDERER
  const StatusCellRenderer = useCallback(
    (props) => {
      const { value } = props;
      const field = props.colDef.field;
      let statusInfo = { text: 'Unknown', color: '#6c757d', bg: '#e2e3e5' };
      try {
        if (field === 'paymentStatus') {
          statusInfo = mapPaymentType(value);
        } else if (field === 'orderStatus') {
          statusInfo = mapOrderStatus(value);
        } else if (field === 'paymentMethod') {
          statusInfo = { text: value ? value : 'UnKnown', color: '#6c757d', bg: '#e2e3e5' };
        }
      } catch {
        statusInfo = { text: 'Error', color: '#dc3545', bg: '#f8d7da' };
      }
      return (
        <span
          style={{
            backgroundColor: statusInfo.bg,
            color: statusInfo.color,
            padding: '6px 12px',
            borderRadius: '16px',
            fontSize: '11px',
            fontWeight: '700',
            border: `1px solid ${statusInfo.color}`,
            display: 'inline-block',
            textAlign: 'center',
            minWidth: '85px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          {statusInfo.text}
        </span>
      );
    },
    [mapPaymentType, mapOrderStatus]
  );

  // ORDER ITEMS CELL RENDERER
  const OrderItemsCellRenderer = useCallback((props) => {
    const orderItems = props.data.orderItems || [];
    const itemCount = orderItems.length;
    const totalQuantity = orderItems.reduce((sum, item) => {
      if (!item.refunded) {
        return sum + (item.quantity || 0);
      } else {
        return sum;
      }
    }, 0);
    const refundedItems = orderItems.filter((item) => item.refunded).length;
    const voidItems = orderItems.filter((item) => item.voidItem).length;
    let primaryColor = '#28a745';
    let primaryBg = '#d4edda';
    if (refundedItems === itemCount) {
      primaryColor = '#dc3545';
      primaryBg = '#f8d7da';
    } else if (voidItems === itemCount) {
      primaryColor = '#856404';
      primaryBg = '#fff3cd';
    } else if (refundedItems > 0 || voidItems > 0) {
      primaryColor = '#fd7e14';
      primaryBg = '#ffeaa7';
    }
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'nowrap' }}>
        <span
          style={{
            backgroundColor: primaryColor,
            color: '#fff',
            padding: '4px 8px',
            borderRadius: '12px',
            fontSize: '11px',
            fontWeight: '600',
            minWidth: '45px',
            textAlign: 'center',
            whiteSpace: 'nowrap',
          }}
        >
          {itemCount} items
        </span>
        <span
          style={{
            backgroundColor: '#E8E2FF',
            color: '#6E5199',
            padding: '4px 6px',
            borderRadius: '10px',
            fontSize: '10px',
            fontWeight: '600',
            minWidth: '35px',
            textAlign: 'center',
            whiteSpace: 'nowrap',
          }}
        >
          Qty: {totalQuantity}
        </span>
        {(refundedItems > 0 || voidItems > 0) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '2px', fontSize: '9px', fontWeight: '600', color: '#6c757d' }}>
            {refundedItems > 0 && (
              <span style={{ backgroundColor: '#dc3545', color: '#fff', padding: '2px 4px', borderRadius: '6px', minWidth: '15px', textAlign: 'center' }}>
                R{refundedItems}
              </span>
            )}
            {voidItems > 0 && (
              <span style={{ backgroundColor: '#856404', color: '#fff', padding: '2px 4px', borderRadius: '6px', minWidth: '15px', textAlign: 'center' }}>
                V{voidItems}
              </span>
            )}
          </div>
        )}
      </div>
    );
  }, []);

  // MODAL AND FORM HANDLERS
  const handlePasscodeSubmit = (isVerified) => {
    if (isVerified) {
      setIsAuthenticated(true);
      setShowPasscodePopup(false);
      if (refundType === 'order') {
        UpdateOrderData();
      } else if (refundType === 'item') {
        processItemRefund();
      }
    }
  };
  const closePasscodePopup = () => {
    setShowPasscodePopup(false);
    setIsAuthenticated(false);
    setRefundType('');
  };
  const handleInputChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  const handleItemRefundInputChange = (e) => setItemRefundData({ ...itemRefundData, [e.target.name]: e.target.value });
  const closeRefundModal = () => {
    setRefundModalOpen(false);
    setFormData({ remarks: '' });
  };

  const openRefundModal = () => {
    if (!selectedRows.originalId) {
      toast.warning('Please select an order first');
      return;
    }
    if (selectedRows.paymentStatus === 3) {
      toast.info('This order is already refunded');
      return;
    }
    if (selectedRows.paymentStatus === 0) {
      toast.info('Cannot refund pending orders');
      return;
    }

    // NEW: Check for partial refunds
    if (hasPartialRefunds(selectedRows)) {
      toast.warning('This order has partial refunds. Please use Item Refund to refund remaining items.');
      return;
    }

    setRefundModalOpen(true);
  };
  const initiateUpdateOrder = () => {
    if (!formData.remarks.trim()) {
      toast.warning('Please provide a refund reason');
      return;
    }
    if (!isAuthenticated) {
      setRefundType('order');
      closeRefundModal();
      setShowPasscodePopup(true);
    } else {
      UpdateOrderData();
    }
  };

  // FULL ORDER REFUND
  const UpdateOrderData = async () => {
    try {
      setLoading(true);
      const itemNumber = selectedRows?.originalId;

      // Build "selectedItems" for ALL refundable lines
      const selectedItemsForReturn = (selectedRows.orderItems || [])
        .filter((li) => !li.refunded && !li.voidItem) // only remaining lines
        .map((li) => ({
          lineKey: `${li.itemId}#${li.itemIndex ?? 0}`,
          quantity: Number(li.quantity || 0),
        }))
        .filter((x) => x.quantity > 0);

      // STEP 1: Call return API for all items (no UI selection needed)
      const returnAPIResult = await callReturnAPI(selectedRows, selectedItemsForReturn);

      if (!returnAPIResult.success) {
        toast.error(`Return API failed: ${returnAPIResult.message}`);
        setLoading(false);
        return;
      }

      toast.success('Return API call successful, processing refund...');

      // STEP 2: Proceed with refund logic
      const dataReq = {
        id: parseInt(itemNumber),
        paymentStatus: 3,
        amountRefunded: parseFloat(selectedRows?.orderAmount || 0),
        refundReason: formData?.remarks,
        loggedInUserId: (await userManagement.getUserName()) || 'Guest',
      };

      const apiService = new ApiService();
      const res = await apiService.put('UPDATE_ORDER', dataReq);

      if (res?.data?.statusCode === 200 || res?.status === 200) {
        toast.success('Order Refunded Successfully');
        invokeBroadCastMessage(`Order Refunded By Cashier. OrderNo#${itemNumber.toString()}`);
        setRefundModalOpen(false);
        setFormData({ remarks: '' });
        setSelectedRows({});
        gridRef.current?.api?.deselectAll();
        GetOrders();
      } else {
        toast.error('Failed to process refund. Please try again.');
      }
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error('Failed to process refund. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // API CALLS
  useEffect(() => {
    GetOrders();
    GetPaymentMethods();
  }, [status]);

  const GetOrders = async () => {
    try {
      setLoading(true);
      const apiService = new ApiService();
      let parameters = status ? `?OrderStatus=${status}` : '';
      let res = await apiService.getApiParamater('GET_ORDERS', parameters);
      if (Array.isArray(res?.data?.result)) {
        const filteredOrders = res.data.result.filter((order) => order?.orderStatus !== 8);
        filteredOrders.reverse();
        setOrderData(filteredOrders);
      } else {
        setOrderData([]);
      }
    } catch (error) {
      console.error('Error fetching Orders:', error);
      toast.error('Failed to fetch orders');
      setOrderData([]);
    } finally {
      setLoading(false);
    }
  };

  // ORDER EDITING FOR REDUX
  const handleEditOrder = (data) => {
    dispatch(clearOrder());
    dispatch(clearCart());

    const orderItems = data?.orderItems || [];
    const orderItemsMeta = data?.orderItemsMeta || [];

    const orderItemsMetaMap = {};
    orderItemsMeta.forEach((orderItemMeta) => {
      const menuItemId = orderItemMeta.menuItemId;
      if (!orderItemsMetaMap[menuItemId]) {
        orderItemsMetaMap[menuItemId] = [];
      }
      orderItemsMetaMap[menuItemId].push(orderItemMeta);
    });

    orderItems.forEach((orderItem, index) => {
      const menuItemId = orderItem.itemId;
      const itemMeta = orderItemsMetaMap[menuItemId] || [];
      const selectedVariant = itemMeta.find((meta) => meta.type === 4) || null;
      const processedItem = {
        itemData: {
          ...orderItem,
          id: orderItem.itemId,
          itemId: orderItem.itemId,
          menuItemName: orderItem.menuItemName || `Item ${orderItem.itemId}`,
          menuItemPrice: orderItem.menuItemPrice,
          orderItemsMeta: itemMeta,
          itemIndex: orderItem.itemIndex || index,
          voidItem: orderItem.voidItem || false,
          refunded: orderItem.refunded || false,
        },
        quantity: orderItem.quantity || 1,
        specialInstructions: orderItem.kitchenInstructions || '',
        selectedVariant: selectedVariant,
        selectedAdOn: [],
        selectedModifier: [],
        void: orderItem.voidItem || false,
        refund: orderItem.refunded || false,
        index: index,
      };
      dispatch(AddToOrder(processedItem));
    });
  };
  const handleShow = (field, data) => {
    if (data.paymentStatus === 0) {
      handleEditOrder(data);
      navigate('/admin/UpdateOrder', { state: data });
    } else {
      navigate(`/admin/orderDetails/${field}`, { state: data });
    }
  };

  // FORMATTERS
  const dateFormatter = (params) => {
    const date = moment(params.value);
    return date.isValid() ? date.format('MMM DD, YYYY HH:mm') : '';
  };
  const currencyFormatter = (params) => Number(params.value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // COLUMN DEFINITIONS
  const columnDefs = [
    {
      headerName: '',
      field: 'select',
      checkboxSelection: true,
      headerCheckboxSelection: false,
      width: 50,
      pinned: 'left',
      lockPosition: true,
      cellStyle: { textAlign: 'center' },
      suppressMovable: true,
      suppressResize: true,
      suppressSorting: true,
      suppressFilter: true,
    },
    {
      headerName: 'Order #',
      field: 'id',
      filter: true,
      floatingFilter: true,
      minWidth: 100,
      cellStyle: { fontWeight: '700', color: '#6E5199', fontSize: '13px', cursor: 'pointer' },
    },
    {
      headerName: 'Sap Order No',
      field: 'salesOrderNumber',
      // filter: true,
      floatingFilter: true,
      minWidth: 130,
      cellStyle: {
        fontWeight: '700',
        color: '#6E5199',
        fontSize: '13px',
        cursor: 'pointer',
      },
    },
    {
      headerName: 'ReferenceID',
      field: 'referenceID',
      // filter: true,
      floatingFilter: true,
      minWidth: 130,
      cellStyle: {
        fontWeight: '700',
        color: '#6E5199',
        fontSize: '13px',
        cursor: 'pointer',
      },
    },

    {
      headerName: 'Date & Time',
      field: 'orderDate',
      filter: 'agDateColumnFilter',
      minWidth: 170,
      floatingFilter: true,
      valueFormatter: dateFormatter,
      cellStyle: { fontSize: '12px', color: '#2c3e50' },
    },
    {
      headerName: 'Customer',
      field: 'customerName',
      filter: true,
      floatingFilter: true,
      minWidth: 130,
      valueFormatter: (p) => p.value || 'Walk-in',
      cellStyle: (p) => ({ fontStyle: p.value ? 'normal' : 'italic', color: p.value ? '#2c3e50' : '#95a5a6', fontWeight: p.value ? '500' : '400' }),
    },
    { headerName: 'Items', field: 'orderItems', minWidth: 180, cellRenderer: OrderItemsCellRenderer, sortable: false, filter: false },
    {
      headerName: 'Amount',
      field: 'orderAmount',
      filter: 'agNumberColumnFilter',
      floatingFilter: true,
      minWidth: 120,
      valueFormatter: currencyFormatter,
      cellStyle: { fontWeight: '700', color: '#28a745', textAlign: 'right', fontSize: '14px' },
    },
    {
      headerName: 'Payment',
      field: 'paymentStatus',
      filter: true,
      floatingFilter: true,
      cellRenderer: StatusCellRenderer,
      minWidth: 130,
      cellStyle: { textAlign: 'center' },
    },
    {
      headerName: 'Payment Method',
      field: 'paymentMethod',
      filter: true,
      floatingFilter: true,
      cellRenderer: StatusCellRenderer,
      minWidth: 130,
      cellStyle: { textAlign: 'center' },
    },
    {
      headerName: 'Status',
      field: 'orderStatus',
      filter: true,
      floatingFilter: true,
      minWidth: 130,
      cellRenderer: StatusCellRenderer,
      cellStyle: { textAlign: 'center' },
    },
    {
      headerName: 'Source',
      field: 'orderSource',
      filter: true,
      floatingFilter: true,
      cellRenderer: (p) => mapOrderSource(p.value),
      minWidth: 110,
      cellStyle: { fontSize: '12px', fontWeight: '500' },
    },
    {
      headerName: 'Actions',
      field: 'actions',
      cellRenderer: ActionsCellRenderer,
      cellRendererParams: {
        onPrint: (data) => {
          handlePrintReceipt(data);
        },
        onItemRefund: (data) => {
          handleItemRefund(data);
        },
      },
      minWidth: 140,
      cellStyle: { textAlign: 'center' },
      suppressMovable: true,
      suppressSorting: true,
      suppressFilter: true,
    },
    {
      headerName: 'View',
      field: 'originalId',
      cellRenderer: BtnCellRenderer,
      cellRendererParams: {
        clicked: (field, data) => {
          handleShow(field, data);
        },
      },
      minWidth: 80,
      pinned: 'right',
      cellStyle: { textAlign: 'center' },
      suppressMovable: true,
    },
  ];

  // EVENT HANDLERS
  const handleStatusChange = (selectedOption) => {
    setstatus(selectedOption?.value || '');
    setSelectedRows({});
    if (gridRef.current) {
      gridRef.current.api.deselectAll();
    }
  };

  const onBtnCSVExport = useCallback(() => {
    gridRef.current.api.exportDataAsCsv({ fileName: `orders_${moment().format('YYYY-MM-DD')}.csv` });
  }, []);
  const onBtExcelExport = useCallback(() => {
    gridRef.current.api.exportDataAsExcel({ fileName: `orders_${moment().format('YYYY-MM-DD')}.xlsx` });
  }, []);

  const transformedOrders = orderData?.map((order) => ({ ...order, originalId: order.id, id: `ORD-${order.id}`, orderAmount: Number(order.orderAmount || 0) }));
  debugger;
  const VoidItem = () => {
    if (!selectedRows.originalId) {
      toast.warning('Please select an order first');
      return;
    }
    handleEditOrder(selectedRows);
    navigate('/Cashier/UpdateOrder', { state: selectedRows });
  };
  debugger;
  const canRefund =
    selectedRows?.originalId &&
    selectedRows.paymentStatus !== 3 &&
    selectedRows.paymentStatus !== 0 &&
    selectedRows.orderStatus !== 7 &&
    !hasPartialRefunds(selectedRows); // NEW: Disable if has partial refunds  const canVoid = selectedRows?.originalId && selectedRows.paymentStatus === 0;
  const canItemRefund =
    selectedRows?.originalId &&
    selectedRows.paymentStatus !== 3 &&
    selectedRows.paymentStatus !== 0 &&
    selectedRows.orderItems &&
    selectedRows.orderItems.length > 0;

  const onGridReady = useCallback(() => {}, []);

  return (
    <>
      {/* Header */}
      <button className="btn addItem-btn m-4" onClick={() => navigate(-1)}>
        ← Back
      </button>
      <div
        className="d-flex justify-content-between align-items-center"
        style={{
          padding: '24px 30px',
          background: '#fff',
          borderRadius: '8px',
          marginBottom: '24px',
          boxShadow: '0px 4px 10px 0px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e9ecef',
        }}
      >
        <div className="d-flex gap-4 align-items-center">
          <div>
            <h4 style={{ color: '#495057', fontWeight: '600', margin: 0, fontSize: '24px' }}>Sales History</h4>
            <p style={{ color: '#6c757d', margin: 0, fontSize: '14px' }}>Manage and track all order transactions</p>
          </div>
          <div style={{ minWidth: '280px' }}>
            <Select
              styles={{
                control: (provided, state) => ({
                  ...provided,
                  borderColor: state.isFocused ? '#6E5199' : '#dee2e6',
                  borderRadius: '6px',
                  minHeight: '38px',
                  border: '1px solid',
                  boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(110, 81, 153, 0.25)' : null,
                  '&:hover': { borderColor: '#6E5199' },
                }),
                placeholder: (provided) => ({ ...provided, color: '#6c757d', fontSize: '14px' }),
                singleValue: (provided) => ({ ...provided, color: '#495057', fontSize: '14px' }),
                option: (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isFocused ? '#6E5199' : '#fff',
                  color: state.isFocused ? '#fff' : '#495057',
                  fontSize: '14px',
                  '&:hover': { backgroundColor: '#6E5199', color: '#fff' },
                }),
              }}
              value={ItemSourse.find((item) => item.value === status)}
              onChange={handleStatusChange}
              options={ItemSourse}
              placeholder="Filter by status..."
              isClearable
            />
          </div>
          {loading && (
            <div className="d-flex align-items-center">
              <div className="spinner-border spinner-border-sm" style={{ color: '#6E5199' }}></div>
              <span style={{ fontSize: '14px', color: '#6c757d', marginLeft: '8px' }}>Loading...</span>
            </div>
          )}
        </div>

        <div className="d-flex gap-3 align-items-center">
          {selectedRows?.id && (
            <div className="d-flex gap-2 align-items-center">
              <span style={{ backgroundColor: '#E8E2FF', color: '#6E5199', padding: '6px 12px', borderRadius: '16px', fontSize: '12px', fontWeight: '600' }}>
                Selected: {selectedRows?.id}
              </span>
              {canRefund && (
                <button
                  onClick={openRefundModal}
                  className="btn btn-outline-danger btn-sm"
                  style={{ fontSize: '13px', fontWeight: '500', padding: '6px 12px' }}
                  title="Refund entire order"
                >
                  Full Refund
                </button>
              )}
              {!canRefund && selectedRows.paymentStatus !== 3 && selectedRows.paymentStatus !== 0 && hasPartialRefunds(selectedRows) && (
                <button
                  disabled
                  className="btn btn-outline-secondary btn-sm"
                  style={{ fontSize: '13px', fontWeight: '500', padding: '6px 12px', opacity: 0.6, cursor: 'not-allowed' }}
                  title="Cannot perform full refund - order has partial refunds. Use Item Refund instead."
                >
                  Full Refund (Disabled)
                </button>
              )}
              {canItemRefund && (
                <button
                  onClick={() => handleItemRefund(selectedRows)}
                  className="btn btn-sm"
                  style={{ fontSize: '13px', fontWeight: '500', padding: '6px 12px', backgroundColor: '#8B7CB6', borderColor: '#8B7CB6', color: '#fff' }}
                >
                  Item Refund
                </button>
              )}
              {canRefund && (
                <button onClick={VoidItem} className="btn btn-outline-secondary btn-sm" style={{ fontSize: '13px', fontWeight: '500', padding: '6px 12px' }}>
                  Edit Order
                </button>
              )}
            </div>
          )}

          <div className="d-flex gap-2">
            <button onClick={onBtExcelExport} className="btn btn-outline-success btn-sm" style={{ fontSize: '13px', fontWeight: '500', padding: '6px 12px' }}>
              Excel Export
            </button>
            <button
              onClick={onBtnCSVExport}
              className="btn btn-sm"
              style={{ fontSize: '13px', fontWeight: '500', padding: '6px 12px', backgroundColor: '#6E5199', borderColor: '#6E5199', color: '#fff' }}
            >
              CSV Export
            </button>
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="px-5">
        <div className="brdor mt-3">
          {orderData.length === 0 && !loading ? (
            <div className="text-center py-5" style={{ padding: '60px 20px' }}>
              <div style={{ fontSize: '48px', opacity: '0.4', color: '#6c757d' }}>📋</div>
              <h5 className="mt-3" style={{ color: '#495057', fontWeight: '500' }}>
                No orders found
              </h5>
              <p style={{ color: '#6c757d', fontSize: '14px' }}>Try adjusting your filters or check back later</p>
            </div>
          ) : (
            <div
              className="ag-theme-alpine"
              style={{
                height: '80vh',
                '--ag-header-background-color': '#f8f9fa',
                '--ag-header-foreground-color': '#495057',
                '--ag-border-color': '#e9ecef',
                '--ag-row-hover-color': '#f8f9ff',
                '--ag-selected-row-background-color': '#E8E2FF',
                '--ag-checkbox-checked-color': '#6E5199',
              }}
            >
              <AgGridReact
                ref={gridRef}
                enableFilter={true}
                floatingFilter={true}
                rowSelection={'single'}
                columnDefs={columnDefs}
                pagination={true}
                paginationPageSize={25}
                rowData={transformedOrders}
                onSelectionChanged={onSelectionChanged}
                onGridReady={onGridReady}
                getRowStyle={(params) => {
                  if (params.data.id === selectedRows?.id) {
                    return { background: '#E8E2FF', borderLeft: '4px solid #6E5199', fontWeight: '500' };
                  }
                  return null;
                }}
                rowHeight={48}
                headerHeight={45}
                animateRows={true}
                suppressRowClickSelection={true}
                loadingOverlayComponent={'Loading orders...'}
                defaultColDef={{ resizable: true, sortable: true, filter: true }}
                rowMultiSelectWithClick={false}
                suppressRowDeselection={false}
                enableRangeSelection={false}
                suppressCellFocus={true}
                getRowId={(params) => params.data.originalId}
              />
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showPasscodePopup && <PasscodePopup onSubmit={handlePasscodeSubmit} onClose={closePasscodePopup} />}

      {/* FULL REFUND MODAL */}
      <Modal size="md" show={RefundModal} onHide={closeRefundModal} backdrop="static" centered>
        <Modal.Header closeButton style={{ background: '#6E5199', color: '#fff', borderBottom: 'none', borderRadius: '12px 12px 0 0' }}>
          <Modal.Title style={{ fontWeight: '600', fontSize: '18px' }}>💰 Refund Order - {selectedRows?.id}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '30px' }}>
          <div className="mb-4">
            <div style={{ background: '#fff3cd', border: '2px solid #f39c12', borderRadius: '12px', padding: '20px' }}>
              <div style={{ color: '#d68910', fontWeight: '700', fontSize: '16px', marginBottom: '12px' }}>⚠️ Refund Details</div>
              <div style={{ color: '#856404', fontSize: '14px', lineHeight: '1.6' }}>
                <div className="mb-2">
                  <strong>Order Amount:</strong>{' '}
                  <span style={{ color: '#28a745', fontWeight: '700' }}>
                    ${Number(selectedRows?.orderAmount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="mb-2">
                  <strong>Customer:</strong> {selectedRows?.customerName || 'Walk-in Customer'}
                </div>
                <div>
                  <strong>Date:</strong> {moment(selectedRows?.orderDate).format('MMM DD, YYYY HH:mm')}
                </div>
              </div>
            </div>
          </div>
          <div>
            <label htmlFor="refundReason" style={{ fontSize: '14px', fontWeight: '600', color: '#2c3e50', marginBottom: '8px', display: 'block' }}>
              Refund Reason *
            </label>
            <textarea
              className="form-control"
              id="refundReason"
              name="remarks"
              value={formData.remarks}
              onChange={handleInputChange}
              rows="4"
              placeholder="Please provide a detailed reason for the refund..."
              required
              style={{ borderRadius: '10px', border: '2px solid #E9ECFF', padding: '12px 16px', fontSize: '14px', transition: 'all 0.3s ease', resize: 'none' }}
            />
            <small style={{ color: '#95a5a6', fontSize: '12px' }}>This reason will be recorded for audit purposes.</small>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ padding: '20px 30px', borderTop: '1px solid #f1f3f4' }}>
          <button
            onClick={closeRefundModal}
            disabled={loading}
            style={{
              background: '#95a5a6',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1,
            }}
          >
            Cancel
          </button>
          <button
            onClick={initiateUpdateOrder}
            disabled={!formData.remarks.trim() || loading}
            style={{
              background: !formData.remarks.trim() || loading ? '#95a5a6' : '#dc3545',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: !formData.remarks.trim() || loading ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              minWidth: '140px',
            }}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>Processing...
              </>
            ) : (
              'Process Refund'
            )}
          </button>
        </Modal.Footer>
      </Modal>

      {/* ITEM REFUND / REPLACEMENT MODAL */}
      <Modal size="xl" show={ItemRefundModal} onHide={closeItemRefundModal} backdrop="static" centered>
        <Modal.Header
          closeButton
          style={{ background: itemRefundData.isReplacement ? '#8B7CB6' : '#6E5199', color: '#fff', borderBottom: 'none', borderRadius: '12px 12px 0 0' }}
        >
          <Modal.Title style={{ fontWeight: '600', fontSize: '18px' }}>
            {itemRefundData.isReplacement ? '🔄 Item Replacement' : '🔄 Item Refund'} - {selectedRows?.id}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '30px', maxHeight: '80vh', overflowY: 'auto' }}>
          {/* Order Summary */}
          <div className="mb-4">
            <div style={{ background: '#f8f9fa', border: '2px solid #6E5199', borderRadius: '12px', padding: '20px' }}>
              <div style={{ color: '#6E5199', fontWeight: '700', fontSize: '16px', marginBottom: '12px' }}>📋 Order Summary</div>
              <div style={{ color: '#495057', fontSize: '14px', lineHeight: '1.6' }}>
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-2">
                      <strong>Order #:</strong> {selectedRows?.id}
                    </div>
                    <div className="mb-2">
                      <strong>Customer:</strong> {selectedRows?.customerName || 'Walk-in Customer'}
                    </div>
                    <div className="mb-2">
                      <strong>Date:</strong> {moment(selectedRows?.orderDate).format('MMM DD, YYYY HH:mm')}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-2">
                      <strong>Total Amount:</strong>{' '}
                      <span style={{ color: '#28a745', fontWeight: '700' }}>
                        ${Number(selectedRows?.orderAmount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className="mb-2">
                      <strong>Selected Items Value:</strong>{' '}
                      <span style={{ color: '#dc3545', fontWeight: '700' }}>
                        ${calculateItemRefundAmount().toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    {itemRefundData.isReplacement && (
                      <div className="mb-2">
                        <strong>Replacement Value:</strong>{' '}
                        <span style={{ color: '#8B7CB6', fontWeight: '700' }}>
                          ${calculateReplacementAmount().toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Refund/Replacement Toggle */}
          <div className="mb-4">
            <div className="d-flex gap-3 align-items-center">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="processType"
                  id="refundType"
                  checked={!itemRefundData.isReplacement}
                  onChange={() => handleReplacementToggle(false)}
                  style={{ transform: 'scale(1.2)', accentColor: '#6E5199' }}
                />
                <label className="form-check-label" htmlFor="refundType" style={{ fontSize: '16px', fontWeight: '600', color: '#6E5199', marginLeft: '8px' }}>
                  🔄 Refund Items
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="processType"
                  id="replacementType"
                  checked={itemRefundData.isReplacement}
                  onChange={() => handleReplacementToggle(true)}
                  style={{ transform: 'scale(1.2)', accentColor: '#8B7CB6' }}
                />
                <label
                  className="form-check-label"
                  htmlFor="replacementType"
                  style={{ fontSize: '16px', fontWeight: '600', color: '#8B7CB6', marginLeft: '8px' }}
                >
                  🔄 Replace Items
                </label>
              </div>
            </div>
          </div>

          {/* Items Selection with Quantity (per line) */}
          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 style={{ color: '#495057', fontWeight: '600', margin: 0 }}>
                🛍️ Select Items to {itemRefundData.isReplacement ? 'Replace' : 'Refund'}
                {itemRefundData.selectedItems.length > 0 && (
                  <span
                    style={{
                      backgroundColor: '#E8E2FF',
                      color: '#6E5199',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: '600',
                      marginLeft: '8px',
                    }}
                  >
                    {itemRefundData.selectedItems.length} selected
                  </span>
                )}
              </h6>
              <div className="d-flex gap-2">
                {selectedRows?.orderItems?.filter((item) => getRemainingQuantityForLine(item) > 0).length > 0 && (
                  <>
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={handleSelectAllItems}
                      style={{ fontSize: '12px', padding: '4px 12px', backgroundColor: '#6E5199', borderColor: '#6E5199', color: '#fff' }}
                    >
                      Select All Available
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={handleDeselectAllItems}
                      disabled={itemRefundData.selectedItems.length === 0}
                      style={{
                        fontSize: '12px',
                        padding: '4px 12px',
                        opacity: itemRefundData.selectedItems.length === 0 ? 0.6 : 1,
                        cursor: itemRefundData.selectedItems.length === 0 ? 'not-allowed' : 'pointer',
                      }}
                    >
                      Clear All
                    </button>
                  </>
                )}
              </div>
            </div>

            <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #e9ecef', borderRadius: '8px', padding: '15px' }}>
              {selectedRows?.orderItems?.map((item, index) => {
                const selected = isLineSelected(item);
                const selectedQty = getSelectedLineQuantity(item);
                const remainingQty = getRemainingQuantityForLine(item);
                const refundHistory = getRefundHistoryText(item.itemId);
                const isDisabled = remainingQty === 0;

                return (
                  <div
                    key={`${item.itemId}-${item.itemIndex ?? index}`}
                    className="d-flex align-items-center justify-content-between p-3 mb-2"
                    style={{
                      background: isDisabled ? '#f8f9fa' : selected ? '#E8E2FF' : '#fff',
                      borderRadius: '8px',
                      border: '2px solid',
                      borderColor: isDisabled ? '#e9ecef' : selected ? '#6E5199' : '#e9ecef',
                      opacity: isDisabled ? 0.5 : 1,
                    }}
                  >
                    <div className="d-flex align-items-center flex-grow-1">
                      <input
                        type="checkbox"
                        className="form-check-input me-3"
                        style={{ transform: 'scale(1.2)', accentColor: '#6E5199' }}
                        checked={selected}
                        onChange={(e) => handleItemSelection(item, e.target.checked)}
                        disabled={isDisabled}
                      />
                      <div className="flex-grow-1">
                        <div style={{ fontWeight: '600', color: '#2c3e50', fontSize: '14px' }}>
                          {item.menuItemName || `Item ${item.itemId}`}
                          {item.refunded && <span style={{ color: '#dc3545', marginLeft: '8px', fontSize: '12px' }}>[REFUNDED]</span>}
                          {item.voidItem && <span style={{ color: '#856404', marginLeft: '8px', fontSize: '12px' }}>[VOID]</span>}
                          {isDisabled && !item.refunded && !item.voidItem && (
                            <span style={{ color: '#dc3545', marginLeft: '8px', fontSize: '12px' }}>[NO REMAINING QTY]</span>
                          )}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>
                          <span style={{ fontWeight: '600', color: remainingQty > 0 ? '#28a745' : '#dc3545' }}>✓ Remaining: {remainingQty}</span>
                          {' | '}
                          Price: ${Number(item.menuItemPrice).toFixed(2)} each
                          {' | '}
                          Line Total: ${(Number(item.menuItemPrice) * Number(item.quantity)).toFixed(2)}
                        </div>
                        {refundHistory && <div style={{ fontSize: '11px', color: '#fd7e14', fontWeight: '600', marginTop: '2px' }}>⚠️ {refundHistory}</div>}
                        {item.kitchenInstructions && (
                          <div style={{ fontSize: '11px', color: '#95a5a6', fontStyle: 'italic', marginTop: '2px' }}>Note: {item.kitchenInstructions}</div>
                        )}
                      </div>
                    </div>

                    {selected && remainingQty > 0 && (
                      <div className="d-flex align-items-center gap-3">
                        <div style={{ minWidth: '140px' }}>
                          <label style={{ fontSize: '11px', color: '#6c757d', display: 'block', marginBottom: '4px', fontWeight: '600' }}>
                            Qty to {itemRefundData.isReplacement ? 'Replace' : 'Refund'}
                          </label>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            min="1"
                            max={remainingQty}
                            value={selectedQty}
                            onChange={(e) => handleItemQuantityChange(item, e.target.value)}
                            style={{
                              width: '90px',
                              textAlign: 'center',
                              fontWeight: '600',
                              fontSize: '13px',
                              borderColor: selectedQty > remainingQty ? '#dc3545' : '#6E5199',
                              borderWidth: '2px',
                            }}
                          />
                          <small style={{ fontSize: '10px', color: selectedQty > remainingQty ? '#dc3545' : '#6c757d', fontWeight: '600' }}>
                            Max: {remainingQty} {selectedQty > remainingQty && '⚠️ Exceeds limit!'}
                          </small>
                        </div>
                        <div style={{ fontWeight: '700', color: '#28a745', fontSize: '14px', minWidth: '80px', textAlign: 'right' }}>
                          ${(Number(item.menuItemPrice) * Math.min(Number(selectedQty), remainingQty)).toFixed(2)}
                        </div>
                      </div>
                    )}

                    {!selected && (
                      <div style={{ fontWeight: '700', color: '#6c757d', fontSize: '14px', minWidth: '80px', textAlign: 'right' }}>
                        ${(Number(item.menuItemPrice) * Number(item.quantity)).toFixed(2)}
                      </div>
                    )}
                  </div>
                );
              })}

              {!selectedRows?.orderItems?.length && (
                <div className="text-center py-3">
                  <span style={{ color: '#6c757d', fontSize: '14px' }}>No items found in this order</span>
                </div>
              )}

              {selectedRows?.orderItems?.length > 0 && selectedRows.orderItems.every((item) => getRemainingQuantityForLine(item) === 0) && (
                <div className="text-center py-3">
                  <div style={{ color: '#dc3545', fontSize: '14px', fontWeight: '600' }}>⚠️ All items have been fully refunded or voided</div>
                  <div style={{ color: '#6c757d', fontSize: '12px', marginTop: '4px' }}>There are no remaining items to refund</div>
                </div>
              )}
            </div>
          </div>

          {/* Replacement Items Section */}
          {itemRefundData.isReplacement && (
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 style={{ color: '#495057', fontWeight: '600', margin: 0 }}>➕ Replacement Items</h6>
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={addReplacementItem}
                  style={{ fontSize: '12px', padding: '4px 12px', backgroundColor: '#8B7CB6', borderColor: '#8B7CB6', color: '#fff' }}
                >
                  + Add Item
                </button>
              </div>

              <div style={{ maxHeight: '250px', overflowY: 'auto', border: '1px solid #e9ecef', borderRadius: '8px', padding: '15px' }}>
                {itemRefundData.replacementItems.map((item) => (
                  <div key={item.id} className="mb-3 p-3" style={{ background: '#E8E2FF', borderRadius: '8px', border: '1px solid #8B7CB6' }}>
                    <div className="row">
                      <div className="col-md-5">
                        <label style={{ fontSize: '12px', fontWeight: '600', color: '#2c3e50', marginBottom: '4px', display: 'block' }}>
                          Select Menu Item *
                        </label>
                        <Select
                          value={item.selectedMenuItem}
                          onChange={(opt) => updateReplacementItem(item.id, 'selectedMenuItem', opt)}
                          options={menuItems}
                          placeholder="Search and select item..."
                          isSearchable
                          isClearable
                          isLoading={loadingMenuItems}
                          loadingMessage={() => 'Loading menu items...'}
                          noOptionsMessage={() => 'No items found'}
                          styles={{
                            control: (provided, state) => ({
                              ...provided,
                              minHeight: '36px',
                              fontSize: '13px',
                              borderColor: state.isFocused ? '#6E5199' : '#dee2e6',
                              boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(110, 81, 153, 0.25)' : null,
                            }),
                            option: (provided, state) => ({
                              ...provided,
                              fontSize: '13px',
                              backgroundColor: state.isFocused ? '#6E5199' : '#fff',
                              color: state.isFocused ? '#fff' : '#495057',
                            }),
                            singleValue: (provided) => ({ ...provided, fontSize: '13px' }),
                            placeholder: (provided) => ({ ...provided, fontSize: '13px', color: '#6c757d' }),
                          }}
                          filterOption={(option, searchText) => {
                            if (!searchText) return true;
                            const searchLower = searchText.toLowerCase();
                            return option.data.searchLabel.includes(searchLower) || option.label.toLowerCase().includes(searchLower);
                          }}
                        />
                      </div>
                      <div className="col-md-2">
                        <label style={{ fontSize: '12px', fontWeight: '600', color: '#2c3e50', marginBottom: '4px', display: 'block' }}>Price</label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={item.selectedMenuItem ? `$${Number(item.selectedMenuItem.salePrice || 0).toFixed(2)}` : '$0.00'}
                          disabled
                          style={{ backgroundColor: '#f8f9fa', fontSize: '13px' }}
                        />
                      </div>
                      <div className="col-md-2">
                        <label style={{ fontSize: '12px', fontWeight: '600', color: '#2c3e50', marginBottom: '4px', display: 'block' }}>Qty *</label>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          value={item.quantity}
                          onChange={(e) => updateReplacementItem(item.id, 'quantity', e.target.value)}
                          placeholder="1"
                          min="1"
                          style={{ fontSize: '13px' }}
                        />
                      </div>
                      <div className="col-md-2">
                        <label style={{ fontSize: '12px', fontWeight: '600', color: '#2c3e50', marginBottom: '4px', display: 'block' }}>Notes</label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={item.notes}
                          onChange={(e) => updateReplacementItem(item.id, 'notes', e.target.value)}
                          placeholder="Optional notes"
                          style={{ fontSize: '13px' }}
                        />
                      </div>
                      <div className="col-md-1 d-flex align-items-end">
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => removeReplacementItem(item.id)}
                          style={{ fontSize: '11px', padding: '4px 8px' }}
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                    <div className="mt-2" style={{ fontSize: '12px', color: '#6E5199', fontWeight: '600' }}>
                      Total: ${((item.selectedMenuItem?.salePrice || 0) * Number(item.quantity || 1)).toFixed(2)}
                    </div>
                  </div>
                ))}

                {itemRefundData.replacementItems.length === 0 && (
                  <div className="text-center py-3">
                    <span style={{ color: '#6c757d', fontSize: '14px' }}>No replacement items added</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Payment Method Selection */}
          {!itemRefundData.isReplacement && calculateNetRefundAmount() > 0 && (
            <div className="mb-4">
              <h6 style={{ color: '#495057', fontWeight: '600', marginBottom: '15px' }}>💳 Refund Payment Method</h6>
              <div className="row">
                <div className="col-md-6">
                  <label style={{ fontSize: '14px', fontWeight: '600', color: '#2c3e50', marginBottom: '8px', display: 'block' }}>Select Payment Method</label>
                  <select
                    className="form-control"
                    value={refundPaymentMethod}
                    onChange={(e) => handleRefundPaymentMethodChange(Number(e.target.value))}
                    style={{ borderRadius: '8px', border: '2px solid #E9ECFF', padding: '12px 16px', fontSize: '14px' }}
                  >
                    <option value={1}>Cash</option>
                    {originalOrderPaymentMethod === 3 && <option value={3}>Flocash (Card Refund)</option>}
                  </select>
                  {originalOrderPaymentMethod === 3 && (
                    <small style={{ color: '#6c757d', fontSize: '12px' }}>
                      Original payment was made via Flocash. You can refund via Cash or initiate Flocash refund.
                    </small>
                  )}
                </div>
                {refundPaymentMethod === 1 && (
                  <div className="col-md-6">
                    <label style={{ fontSize: '14px', fontWeight: '600', color: '#2c3e50', marginBottom: '8px', display: 'block' }}>Cash Amount Given</label>
                    <input
                      type="number"
                      className="form-control"
                      value={refundAmountCollected}
                      onChange={(e) => handleRefundAmountChange(parseFloat(e.target.value) || 0)}
                      placeholder="Enter cash amount given"
                      style={{ borderRadius: '8px', border: '2px solid #E9ECFF', padding: '12px 16px', fontSize: '14px' }}
                    />
                    {refundChange > 0 && <small style={{ color: '#28a745', fontSize: '12px' }}>Change to return: ${refundChange.toFixed(2)}</small>}
                  </div>
                )}
              </div>

              {refundPaymentMethod === 3 && (
                <div className="mt-3">
                  <div style={{ background: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: '8px', padding: '15px' }}>
                    <div style={{ color: '#856404', fontWeight: '600', marginBottom: '8px' }}>⚠️ Flocash Refund Process</div>
                    <div style={{ color: '#856404', fontSize: '14px', marginBottom: '15px' }}>
                      This will initiate a refund back to the original card used for payment. The refund amount of ${calculateNetRefundAmount().toFixed(2)} will
                      be processed through Flocash.
                    </div>
                    <PaymentComponent
                      amount={calculateNetRefundAmount()}
                      Currency={Currency}
                      transId={originalTransactionId}
                      onPaymentSuccess={handleFlocashRefundSuccess}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Reason */}
          <div className="mb-4">
            <label htmlFor="itemRefundReason" style={{ fontSize: '14px', fontWeight: '600', color: '#2c3e50', marginBottom: '8px', display: 'block' }}>
              {itemRefundData.isReplacement ? 'Replacement' : 'Refund'} Reason *
            </label>
            <textarea
              className="form-control"
              id="itemRefundReason"
              name="refundReason"
              value={itemRefundData.refundReason}
              onChange={handleItemRefundInputChange}
              rows="3"
              placeholder={`Please provide a reason for ${itemRefundData.isReplacement ? 'replacing' : 'refunding'} the selected items...`}
              required
              style={{ borderRadius: '8px', border: '2px solid #E9ECFF', padding: '12px 16px', fontSize: '14px', resize: 'none' }}
            />
          </div>

          {/* Summary */}
          {itemRefundData.selectedItems.length > 0 && (
            <div className="mb-3">
              <div
                style={{
                  background: itemRefundData.isReplacement ? '#E8E2FF' : '#f8f9fa',
                  border: itemRefundData.isReplacement ? '2px solid #8B7CB6' : '2px solid #6E5199',
                  borderRadius: '12px',
                  padding: '15px',
                }}
              >
                <div style={{ color: itemRefundData.isReplacement ? '#8B7CB6' : '#6E5199', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                  💰 {itemRefundData.isReplacement ? 'Replacement' : 'Refund'} Summary
                </div>
                <div style={{ color: '#495057', fontSize: '13px' }}>
                  <div>
                    Total quantity to {itemRefundData.isReplacement ? 'replace' : 'refund'}:{' '}
                    <strong>{itemRefundData.selectedItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0)}</strong>
                  </div>
                  <div>
                    Items value: <strong>${calculateItemRefundAmount().toFixed(2)}</strong>
                  </div>
                  {itemRefundData.isReplacement && (
                    <>
                      <div>
                        Replacement items: <strong>{itemRefundData.replacementItems.length}</strong>
                      </div>
                      <div>
                        Replacement value: <strong>${calculateReplacementAmount().toFixed(2)}</strong>
                      </div>
                    </>
                  )}
                  <div>
                    Net refund amount: <strong>${calculateNetRefundAmount().toFixed(2)}</strong>
                  </div>
                  {!itemRefundData.isReplacement && calculateNetRefundAmount() > 0 && (
                    <div>
                      Payment method: <strong>{paymentMethod.find((p) => p.id === refundPaymentMethod)?.name || 'Cash'}</strong>
                    </div>
                  )}
                  <div>
                    Remaining order value:{' '}
                    <strong>${(Number(selectedRows?.orderAmount || 0) - calculateNetRefundAmount() + calculateReplacementAmount()).toFixed(2)}</strong>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ padding: '20px 30px', borderTop: '1px solid #f1f3f4' }}>
          <button
            onClick={closeItemRefundModal}
            disabled={itemRefundLoading}
            style={{
              background: '#95a5a6',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: itemRefundLoading ? 'not-allowed' : 'pointer',
              opacity: itemRefundLoading ? 0.6 : 1,
            }}
          >
            Cancel
          </button>
          <button
            onClick={executeItemRefund}
            disabled={
              !itemRefundData.selectedItems.length ||
              !itemRefundData.refundReason.trim() ||
              (itemRefundData.isReplacement &&
                (itemRefundData.replacementItems.length === 0 ||
                  itemRefundData.replacementItems.some((item) => !item.selectedMenuItem || item.quantity <= 0))) ||
              (!itemRefundData.isReplacement &&
                refundPaymentMethod === 1 &&
                calculateNetRefundAmount() > 0 &&
                refundAmountCollected < calculateNetRefundAmount()) ||
              itemRefundLoading
            }
            style={{
              background:
                !itemRefundData.selectedItems.length ||
                !itemRefundData.refundReason.trim() ||
                (itemRefundData.isReplacement &&
                  (itemRefundData.replacementItems.length === 0 ||
                    itemRefundData.replacementItems.some((item) => !item.selectedMenuItem || item.quantity <= 0))) ||
                (!itemRefundData.isReplacement &&
                  refundPaymentMethod === 1 &&
                  calculateNetRefundAmount() > 0 &&
                  refundAmountCollected < calculateNetRefundAmount()) ||
                itemRefundLoading
                  ? '#95a5a6'
                  : itemRefundData.isReplacement
                  ? '#8B7CB6'
                  : '#6E5199',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: '600',
              cursor:
                !itemRefundData.selectedItems.length ||
                !itemRefundData.refundReason.trim() ||
                (itemRefundData.isReplacement &&
                  (itemRefundData.replacementItems.length === 0 ||
                    itemRefundData.replacementItems.some((item) => !item.selectedMenuItem || item.quantity <= 0))) ||
                (!itemRefundData.isReplacement &&
                  refundPaymentMethod === 1 &&
                  calculateNetRefundAmount() > 0 &&
                  refundAmountCollected < calculateNetRefundAmount()) ||
                itemRefundLoading
                  ? 'not-allowed'
                  : 'pointer',
              transition: 'all 0.3s ease',
              minWidth: '140px',
            }}
          >
            {itemRefundLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>Processing...
              </>
            ) : itemRefundData.isReplacement ? (
              '🔄 Replace Items'
            ) : (
              '🔄 Refund Items'
            )}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Print Receipt Modal */}
      <Modal size="lg" show={PrintModal} onHide={closePrintModal} backdrop="static" centered>
        <Modal.Header closeButton style={{ background: '#6E5199', color: '#fff', borderBottom: 'none', borderRadius: '12px 12px 0 0' }}>
          <Modal.Title style={{ fontWeight: '600', fontSize: '18px' }}>🖨️ Print Receipt - {selectedRows?.id}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '30px' }}>
          {/* Order Summary */}
          <div className="mb-4">
            <div style={{ background: '#f8f9fa', border: '2px solid #6E5199', borderRadius: '12px', padding: '20px' }}>
              <div style={{ color: '#6E5199', fontWeight: '700', fontSize: '16px', marginBottom: '12px' }}>📋 Order Summary</div>
              <div style={{ color: '#495057', fontSize: '14px', lineHeight: '1.6' }}>
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-2">
                      <strong>Order #:</strong> {selectedRows?.id}
                    </div>
                    <div className="mb-2">
                      <strong>Customer:</strong> {selectedRows?.customerName || 'Walk-in Customer'}
                    </div>
                    <div className="mb-2">
                      <strong>Date:</strong> {moment(selectedRows?.orderDate).format('MMM DD, YYYY HH:mm')}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-2">
                      <strong>Amount:</strong>{' '}
                      <span style={{ color: '#28a745', fontWeight: '700' }}>
                        ${Number(selectedRows?.orderAmount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className="mb-2">
                      <strong>Items:</strong> {selectedRows?.orderItems?.length || 0}
                    </div>
                    <div className="mb-2">
                      <strong>Type:</strong> {mapOrderType(selectedRows?.orderType)}
                    </div>
                    {selectedRows?.amountRefunded > 0 && (
                      <div className="mb-2">
                        <strong>Refunded:</strong>{' '}
                        <span style={{ color: '#dc3545', fontWeight: '700' }}>${Number(selectedRows.amountRefunded).toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Printer Settings */}
          {/* <div className="mb-4">
            <h6 style={{ color: '#495057', fontWeight: '600', marginBottom: '15px' }}>🖨️ Printer Settings</h6>
            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label style={{ fontSize: '14px', fontWeight: '600', color: '#2c3e50', marginBottom: '5px', display: 'block' }}>Printer IP Address *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={printerSettings.printerIP}
                    onChange={(e) => handlePrinterSettingsChange('printerIP', e.target.value)}
                    placeholder="192.168.1.100"
                    style={{ borderRadius: '8px', border: '2px solid #E9ECFF', padding: '8px 12px', fontSize: '14px' }}
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label style={{ fontSize: '14px', fontWeight: '600', color: '#2c3e50', marginBottom: '5px', display: 'block' }}>Printer Port *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={printerSettings.printerPort}
                    onChange={(e) => handlePrinterSettingsChange('printerPort', e.target.value)}
                    placeholder="9100"
                    style={{ borderRadius: '8px', border: '2px solid #E9ECFF', padding: '8px 12px', fontSize: '14px' }}
                  />
                </div>
              </div>
            </div>
          </div> */}

          {/* Print Options */}
          {/* <div className="mb-4">
            <h6 style={{ color: '#495057', fontWeight: '600', marginBottom: '15px' }}>⚙️ Print Options</h6>
            <div className="row">
              <div className="col-md-4">
                <div className="mb-3">
                  <label style={{ fontSize: '14px', fontWeight: '600', color: '#2c3e50', marginBottom: '5px', display: 'block' }}>Number of Copies</label>
                  <input
                    type="number"
                    className="form-control"
                    min="1"
                    max="5"
                    value={printerSettings.copies}
                    onChange={(e) => handlePrinterSettingsChange('copies', parseInt(e.target.value) || 1)}
                    style={{ borderRadius: '8px', border: '2px solid #E9ECFF', padding: '8px 12px', fontSize: '14px' }}
                  />
                </div>
              </div>
              <div className="col-md-4">
                <div className="mb-3">
                  <div className="form-check" style={{ marginTop: '30px' }}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="includeOriginal"
                      checked={printerSettings.includeOriginal}
                      onChange={(e) => handlePrinterSettingsChange('includeOriginal', e.target.checked)}
                      style={{ transform: 'scale(1.2)', accentColor: '#6E5199' }}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="includeOriginal"
                      style={{ fontSize: '14px', fontWeight: '500', color: '#2c3e50', marginLeft: '8px' }}
                    >
                      Print Original Receipt
                    </label>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="mb-3">
                  <div className="form-check" style={{ marginTop: '30px' }}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="includeDuplicate"
                      checked={printerSettings.includeDuplicate}
                      onChange={(e) => handlePrinterSettingsChange('includeDuplicate', e.target.checked)}
                      style={{ transform: 'scale(1.2)', accentColor: '#6E5199' }}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="includeDuplicate"
                      style={{ fontSize: '14px', fontWeight: '500', color: '#2c3e50', marginLeft: '8px' }}
                    >
                      Print Duplicate Receipt
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div> */}

          {/* Print Summary */}
          <div className="mb-3">
            <div style={{ background: '#E8E2FF', border: '2px solid #6E5199', borderRadius: '12px', padding: '15px' }}>
              <div style={{ color: '#6E5199', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>📝 Print Summary</div>
              <div style={{ color: '#495057', fontSize: '13px' }}>
                {printerSettings.includeOriginal && `✓ Original receipt (${printerSettings.copies} ${printerSettings.copies === 1 ? 'copy' : 'copies'})`}
                {printerSettings.includeOriginal && printerSettings.includeDuplicate && <br />}
                {printerSettings.includeDuplicate && `✓ Duplicate receipt (${printerSettings.copies} ${printerSettings.copies === 1 ? 'copy' : 'copies'})`}
                <br />
                <strong>
                  {' '}
                  Total prints:{' '}
                  {(printerSettings.includeOriginal ? printerSettings.copies : 0) + (printerSettings.includeDuplicate ? printerSettings.copies : 0)}{' '}
                </strong>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ padding: '20px 30px', borderTop: '1px solid #f1f3f4' }}>
          <button
            onClick={closePrintModal}
            disabled={printLoading}
            style={{
              background: '#95a5a6',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: printLoading ? 'not-allowed' : 'pointer',
              opacity: printLoading ? 0.6 : 1,
            }}
          >
            Cancel
          </button>
          {/* Existing ESC/POS / IP-printer button */}
          <button
            onClick={executePrint}
            disabled={!printerSettings.printerIP || !printerSettings.printerPort || (!printerSettings.id && !printerSettings.includeDuplicate) || printLoading}
            style={{
              background:
                !printerSettings.printerIP ||
                !printerSettings.printerPort ||
                (!printerSettings.includeOriginal && !printerSettings.includeDuplicate) ||
                printLoading
                  ? '#95a5a6'
                  : '#6E5199',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: '600',
              cursor:
                !printerSettings.printerIP ||
                !printerSettings.printerPort ||
                (!printerSettings.includeOriginal && !printerSettings.includeDuplicate) ||
                printLoading
                  ? 'not-allowed'
                  : 'pointer',
              transition: 'all 0.3s ease',
              minWidth: '140px',
            }}
          >
            {printLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>Printing...
              </>
            ) : (
              '🧾 POS Print'
            )}
          </button>
          {/* 👇 NEW: Browser / OS printer button */}
          <button
            onClick={executeBrowserPrint}
            disabled={printLoading}
            style={{
              background: '#ffffff',
              color: '#6E5199',
              border: '1px solid #6E5199',
              borderRadius: '8px',
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: '600',
              marginLeft: '8px',
              marginRight: '8px',
              cursor: printLoading ? 'not-allowed' : 'pointer',
              opacity: printLoading ? 0.6 : 1,
              minWidth: '180px',
            }}
          >
            🖨️ Print
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

// Enhanced Button Cell Renderer
class BtnCellRenderer extends Component {
  constructor(props) {
    super(props);
    this.btnClickedHandler = this.btnClickedHandler.bind(this);
  }
  btnClickedHandler() {
    const { value, clicked, data } = this.props;
    clicked(value, data);
  }
  render() {
    const { paymentStatus } = this.props.data;
    const isPending = paymentStatus === 0;
    return (
      <button
        onClick={this.btnClickedHandler}
        className={isPending ? 'AddBtn' : 'btn btn-outline-primary'}
        style={{
          fontSize: '12px',
          padding: '4px 12px',
          fontWeight: '500',
          borderRadius: '4px',
          minWidth: '70px',
          backgroundColor: isPending ? '#6E5199' : 'transparent',
          borderColor: isPending ? '#6E5199' : '#6E5199',
          color: isPending ? '#fff' : '#6E5199',
        }}
      >
        {isPending ? 'Proceed' : 'View'}
      </button>
    );
  }
}

// Actions Cell Renderer for Print and Item Refund
class ActionsCellRenderer extends Component {
  constructor(props) {
    super(props);
    this.handlePrint = this.handlePrint.bind(this);
    this.handleItemRefund = this.handleItemRefund.bind(this);
  }
  handlePrint() {
    const { onPrint, data } = this.props;
    onPrint(data);
  }
  handleItemRefund() {
    const { onItemRefund, data } = this.props;
    onItemRefund(data);
  }
  render() {
    const { data } = this.props;
    const canItemRefund = data.paymentStatus !== 3 && data.paymentStatus !== 0 && data.orderItems && data.orderItems.length > 0;
    return (
      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
        <button
          onClick={this.handlePrint}
          className="btn btn-outline-success btn-sm"
          style={{ fontSize: '11px', padding: '4px 8px', fontWeight: '500', borderRadius: '4px', minWidth: '50px' }}
          title="Print Receipt"
        >
          🖨️
        </button>
        {canItemRefund && (
          <button
            onClick={this.handleItemRefund}
            className="btn btn-sm"
            style={{
              fontSize: '11px',
              padding: '4px 8px',
              fontWeight: '500',
              borderRadius: '4px',
              minWidth: '50px',
              backgroundColor: '#8B7CB6',
              borderColor: '#8B7CB6',
              color: '#fff',
            }}
            title="Refund Items"
          >
            🔄
          </button>
        )}
      </div>
    );
  }
}

export default Order;
